import Cuboid from '../rendering/Cuboid.js';
import Material from '../materials/Material.js';
import MaterialGroupManager from '../materials/MaterialGroupManager.js';
import MaterialGroup from '../materials/MaterialGroup.js';
import Transform from '../math/Transform.js';
import ProjectionUtils from '../math/ProjectionUtils.js';
import KDTree from './KDTree.js';
import AmbientOcclusion from '../ao/AmbientOcclusion.js';
import FaceOcclusion from '../occlusion/FaceOcclusion.js';
import Debug from '../utils/Debug.js';
import DOMUtils from '../utils/DOMUtils.js';
import WaterIntersectionManager from '../character/WaterIntersectionManager.js';

/**
 * Manages the game world and cuboids
 */
export default class World {
  /**
   * Create a new world
   * @param {Camera} camera - The camera
   */
  constructor(camera) {
    this.camera = camera;
    this.element = document.getElementById('world');
    this.cuboids = new Map();
    this.cubeSize = camera.CUBE_SIZE;

    // Character entities (players and NPCs)
    this.characters = new Map();

    // Ground effects (destination markers, selection circles, etc.)
    this.groundEffects = new Map();

    // Material group manager for multi-face materials
    this.materialGroupManager = new MaterialGroupManager(this);

    // K-D tree for lazy cuboid subdivision to resolve depth cycles
    this.kdTree = new KDTree();

    // Track which cuboid splits were triggered by sprite cycles (cleared each frame)
    this._spriteTriggeredSplits = new Set();

    // Track camera state for fragment clearing
    this.lastCameraQuadrant = null;
    this.fragmentsNeedClearing = false;

    // Track when impostor AO needs resampling (set on cuboid add/remove)
    this._impostorAODirty = false;

    // For future level generation
    this.viewDistance = 10; // How far to render in each direction
    this.chunkSize = 16;    // Size of each generated chunk

    // Initialize face occlusion system
    FaceOcclusion.initialize(this);

    // Character collision system reference (set by main.js)
    this.collisionSystem = null;

    // NPC manager reference (set by main.js)
    this.npcManager = null;

    // Pending NPC data from loaded world (processed async by main.js)
    this._pendingNPCData = null;

    // Vegetation impostors (billboard sprites)
    this.impostors = new Map();

    // Vegetation visibility flag (toggled from Display settings)
    this._vegetationVisible = true;

    // DOM update strategy: true = LIS-based virtual DOM reconciliation,
    // false = innerHTML clear + fragment rebuild (legacy, may be faster on some systems)
    this.useVirtualDOM = true;

    // Sort strategy: true = rate-limited sort (rotation/movement deferred to 10-20Hz),
    // false = sort every frame when rotation or movement changes (legacy)
    this.deferredSort = false;

    // Pending impostor data from loaded world
    this._pendingImpostorData = null;

    // Water intersection manager for sprite clipping in water
    this.waterIntersectionManager = new WaterIntersectionManager(this);

    // Callback for world load events
    this.onWorldLoaded = null;

    // Callback for geometry changes (cuboid add/remove/move)
    // Called with a small delay to batch multiple rapid changes
    this.onGeometryChanged = null;

    // Callback for network sync (fires on every world mutation with operation details)
    this.onMutation = null;
    this._geometryChangeTimeout = null;

    // Scene-level render skip optimization
    this._dirtyCuboids = new Set();
    this._forceRender = true; // Force first render
    this._needsFullSort = true; // Full SAT sort needed (rotation/geometry change)
    this._lastSortTime = 0; // Rate-limiting for movement-only sorts (~20Hz)
    this._androidAODirty = false; // Android: re-apply AO only when zoom changed (not every frame)
    this._lastAOZoom = null; // Track zoom for Android AO refresh

    // Monotonic counter for sort position IDs (replaces Math.random())
    this._sortIdCounter = 0;

    // Reusable submerged attachments map (cleared each frame instead of reallocated)
    this._submergedAttachments = new Map();

    // Reusable per-frame data structures to avoid GC pressure from allocations every frame.
    // Over long sessions (8+ hours), per-frame allocations of Maps/Sets/Arrays fragment
    // V8's heap and can cause OOM crashes.
    this._framePositions = [];
    this._frameDependencies = new Map();
    this._frameCuboidKeyToEntries = new Map();
    this._frameRenderedAttachments = new Set();
    this._frameDesiredOrder = [];
    this._frameDesiredSet = new Set();
    this._frameCurPosMap = new Map();
    this._frameStableSet = new Set();
  }

  /**
   * Notify that geometry has changed (cuboid added/removed/moved)
   * Uses debouncing to batch multiple rapid changes
   * @private
   */
  _notifyGeometryChanged() {
    if (!this.onGeometryChanged) return;

    // Clear any pending notification
    if (this._geometryChangeTimeout) {
      clearTimeout(this._geometryChangeTimeout);
    }

    // Debounce: wait 100ms for changes to settle before notifying
    this._geometryChangeTimeout = setTimeout(() => {
      this._geometryChangeTimeout = null;
      if (this.onGeometryChanged) {
        this.onGeometryChanged();
      }
    }, 100);
  }

  /**
   * Serialize a cuboid's materials to JSON for network sync.
   * @param {Cuboid} cuboid
   * @returns {Object} face -> material JSON
   * @private
   */
  _serializeMaterials(cuboid) {
    const result = {};
    const faces = ['top', 'bottom', 'front', 'back', 'left', 'right'];
    for (const face of faces) {
      if (cuboid.materials[face]) {
        result[face] = cuboid.materials[face].toJSON();
      }
    }
    return result;
  }

  /**
   * Set the collision system reference for physics invalidation
   * @param {CharacterCollisionSystem} collisionSystem
   */
  setCollisionSystem(collisionSystem) {
    this.collisionSystem = collisionSystem;
  }

  /**
   * Check if any cuboids need updating
   * @returns {boolean}
   * @private
   */
  _hasDirtyCuboids() {
    return this._dirtyCuboids.size > 0;
  }

  /**
   * Mark a cuboid as needing re-render
   * @param {Cuboid} cuboid
   */
  markCuboidDirty(cuboid) {
    this._dirtyCuboids.add(cuboid);
  }

  /**
   * Force a full render (with SAT sort) on the next frame
   * Use when geometry changes require depth re-sorting
   */
  forceRender() {
    this._forceRender = true;
    this._needsFullSort = true;
  }

  /**
   * Force a lightweight transform update on the next frame (no SAT sort).
   * Use when only visual state changed (e.g. selection highlights).
   */
  forceTransformUpdate() {
    this._forceRender = true;
  }

  /**
   * Request a full SAT sort on the next render
   * Called when rotation or geometry changes affect depth order
   */
  requestFullSort() {
    this._needsFullSort = true;
  }

  /**
   * Add a character to the world
   * @param {Character} character - The character to add
   */
  addCharacter(character) {
    character.world = this;
    this.characters.set(character.id, character);

    // Attach sprite to world element if it exists
    if (character.sprite) {
      character.sprite.attachTo(this.element);
    }

    // Force re-sort so sprite is depth-ordered with cuboids
    this.forceRender();

    return character;
  }

  /**
   * Remove a character from the world
   * @param {string} characterId - The character's ID
   * @returns {boolean} True if removed, false if not found
   */
  removeCharacter(characterId) {
    const character = this.characters.get(characterId);
    if (character) {
      character.destroy();
      this.characters.delete(characterId);
      this.forceRender();
      return true;
    }
    return false;
  }

  /**
   * Get a character by ID
   * @param {string} characterId - The character's ID
   * @returns {Character|undefined} The character or undefined
   */
  getCharacter(characterId) {
    return this.characters.get(characterId);
  }

  /**
   * Update all characters
   * @param {number} deltaTime - Time since last frame in seconds
   * @returns {boolean} Whether any character is moving (requires re-render)
   */
  updateCharacters(deltaTime) {
    let anyMoving = false;
    for (const character of this.characters.values()) {
      character.update(deltaTime, this.camera);
      if (character.isMoving || (character.pathFollower && character.pathFollower.isFollowing)) {
        anyMoving = true;
      }
    }
    return anyMoving;
  }

  /**
   * Add a ground effect to the world
   * @param {GroundEffect} effect - The ground effect to add
   * @returns {GroundEffect} The added effect
   */
  addGroundEffect(effect) {
    effect.world = this;
    this.groundEffects.set(effect.id, effect);

    // Attach to world element for proper depth sorting
    effect.attachTo(this.element);

    // Trigger a full sort next frame so the effect gets positioned via updateTransform
    // (without this, the element stays at default CSS position until something else triggers a sort)
    this._needsFullSort = true;

    return effect;
  }

  /**
   * Remove a ground effect from the world
   * @param {string} effectId - The effect's ID
   * @returns {boolean} True if removed, false if not found
   */
  removeGroundEffect(effectId) {
    const effect = this.groundEffects.get(effectId);
    if (effect) {
      effect.destroy();
      this.groundEffects.delete(effectId);
      return true;
    }
    return false;
  }

  /**
   * Get a ground effect by ID
   * @param {string} effectId - The effect's ID
   * @returns {GroundEffect|undefined} The effect or undefined
   */
  getGroundEffect(effectId) {
    return this.groundEffects.get(effectId);
  }

  /**
   * Add an impostor to the world
   * @param {Impostor} impostor - The impostor to add
   * @returns {Impostor} The added impostor
   */
  addImpostor(impostor) {
    impostor.world = this;
    this.impostors.set(impostor.id, impostor);
    // Mark vegetation impostors with a CSS class so the vegetation-hidden rule
    // can hide them even if sort/update code re-inserts or re-styles elements.
    if (impostor._isVegetation && impostor.element) {
      impostor.element.classList.add('vegetation-impostor');
    }
    impostor.attachTo(this.element);
    // Sample AO immediately — if floor AO is already cached, item appears correctly lit
    if (AmbientOcclusion.enabled) {
      impostor.sampleAO();
    }
    this.forceRender();

    // Notify network sync (skip projectile impostors and dungeon-local impostors)
    if (this.onMutation && !impostor._isProjectile && !impostor._skipSync) {
      this.onMutation({
        op: 'addImpostor',
        data: impostor.toJSON()
      });
    }

    return impostor;
  }

  /**
   * Remove an impostor from the world
   * @param {string} impostorId - The impostor's ID
   * @returns {boolean} True if removed, false if not found
   */
  removeImpostor(impostorId) {
    const impostor = this.impostors.get(impostorId);
    if (impostor) {
      const isProjectile = impostor._isProjectile;
      const skipSync = impostor._skipSync;
      impostor.destroy();
      this.impostors.delete(impostorId);
      this.forceRender();

      // Notify network sync (skip projectile impostors and dungeon-local impostors)
      if (this.onMutation && !isProjectile && !skipSync) {
        this.onMutation({
          op: 'removeImpostor',
          data: { id: impostorId }
        });
      }

      return true;
    }
    return false;
  }

  /**
   * Get an impostor by ID
   * @param {string} impostorId - The impostor's ID
   * @returns {Impostor|undefined} The impostor or undefined
   */
  getImpostor(impostorId) {
    return this.impostors.get(impostorId);
  }

  /**
   * Toggle visibility of vegetation impostors (non-dynamic sprites like trees, grass).
   * When hidden, vegetation is excluded from the sort pass and DOM-hidden.
   * @param {boolean} visible
   */
  setVegetationVisible(visible) {
    this._vegetationVisible = visible;
    // CSS class provides browser-enforced hiding that survives any element-level
    // display changes from sort/update code re-inserting or re-styling elements.
    if (visible) {
      this.element.classList.remove('vegetation-hidden');
    } else {
      this.element.classList.add('vegetation-hidden');
    }
    for (const impostor of this.impostors.values()) {
      if (impostor._isVegetation) {
        if (!visible) {
          impostor.clearWaterClip();
          impostor.element.style.display = 'none';
        }
      }
    }
    this.forceRender();
  }

  /**
   * Check if an impostor's virtual 1x1x1 sorting cuboid would intersect any solid cuboid.
   * Skips noCollision cuboids (water, gas) to allow placement inside transparent volumes.
   * @param {{ x: number, y: number, z: number }} pos - Impostor center position
   * @returns {boolean} True if would intersect a solid cuboid
   */
  wouldImpostorIntersect(pos) {
    const EPS = 0.01;
    const minX = pos.x - 0.5;
    const maxX = pos.x + 0.5;
    const minY = pos.y;
    const maxY = pos.y + 1;
    const minZ = pos.z - 0.5;
    const maxZ = pos.z + 0.5;

    for (const cuboid of this.cuboids.values()) {
      if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;

      const cMaxX = cuboid.x + cuboid.width;
      const cMaxY = cuboid.y + cuboid.height;
      const cMaxZ = cuboid.z + cuboid.depth;

      if (minX < cMaxX - EPS && maxX > cuboid.x + EPS &&
          minY < cMaxY - EPS && maxY > cuboid.y + EPS &&
          minZ < cMaxZ - EPS && maxZ > cuboid.z + EPS) {
        return true;
      }
    }
    return false;
  }

  /**
   * Find all impostors sitting on a given cuboid's top surface.
   * @param {Cuboid} cuboid - The cuboid to check
   * @returns {Impostor[]} Array of impostors on this cuboid
   */
  getImpostorsOnCuboid(cuboid) {
    const topY = cuboid.y + cuboid.height;
    const result = [];

    for (const impostor of this.impostors.values()) {
      if (impostor.x >= cuboid.x && impostor.x < cuboid.x + cuboid.width &&
          impostor.z >= cuboid.z && impostor.z < cuboid.z + cuboid.depth &&
          Math.abs(impostor.y - topY) <= 0.5) {
        result.push(impostor);
      }
    }
    return result;
  }

  /**
   * Find water cuboids (noCollision + transparent) that overlap an impostor's virtual bounds.
   * @param {Impostor} impostor
   * @returns {Cuboid[]}
   */
  _findImpostorWaterCuboids(impostor) {
    // Use visual extent for detection: the billboard sprite can be wider than
    // the 1x1 sort footprint, so we need to detect water cuboids that the
    // visual overlaps even if the sort AABB doesn't reach them.
    const visualWidth = impostor.scale * (impostor.spriteWidth / impostor.spriteHeight);
    const halfW = Math.max(impostor.sortWidth, visualWidth) / 2;
    const halfD = Math.max(impostor.sortDepth, visualWidth) / 2;
    const impMinX = impostor.x - halfW;
    const impMaxX = impostor.x + halfW;
    const impMinY = impostor.y;
    const impMaxY = impostor.y + impostor.scale;
    const impMinZ = impostor.z - halfD;
    const impMaxZ = impostor.z + halfD;

    const result = [];
    for (const cuboid of this.cuboids.values()) {
      if (!cuboid.hasNoCollision || !cuboid.hasNoCollision()) continue;
      if (!cuboid.hasNonOpaqueFace || !cuboid.hasNonOpaqueFace()) continue;

      if (impMinX < cuboid.x + cuboid.width && impMaxX > cuboid.x &&
          impMinY < cuboid.y + cuboid.height && impMaxY > cuboid.y &&
          impMinZ < cuboid.z + cuboid.depth && impMaxZ > cuboid.z) {
        result.push(cuboid);
      }
    }
    return result;
  }

  /**
   * Re-sample AO for all impostors (call after AO rebuild)
   */
  resampleImpostorAO() {
    for (const impostor of this.impostors.values()) {
      impostor.sampleAO();
    }
  }

  /**
   * Add a cuboid to the world
   * @param {number} x - X coordinate (horizontal position)
   * @param {number} y - Y coordinate (height position)
   * @param {number} z - Z coordinate (depth position)
   * @param {number} width - Width (X-axis)
   * @param {number} height - Height (Y-axis)
   * @param {number} depth - Depth (Z-axis)
   * @param {Object} materials - Materials for each face
   * @param {Object} textureOrigin - Optional texture anchor point for preserving texture position during resize
   * @return {Cuboid} The created cuboid
   */
  addCuboid(x, y, z, width = 1, height = 1, depth = 1, materials = null, textureOrigin = null) {
    const options = {
      x, y, z, width, height, depth,
      cubeSize: this.cubeSize,
      materials
    };
    // Preserve texture origin for world-stationary resize behavior
    if (textureOrigin) {
      options.textureOrigin = textureOrigin;
    }
    const cuboid = new Cuboid(options);

    this.cuboids.set(cuboid.getKey(), cuboid);
    this.element.appendChild(cuboid.element);

    // Flag fragments for clearing since scene changed
    this.fragmentsNeedClearing = true;

    // Update AO voxel grid and invalidate affected area
    if (AmbientOcclusion.enabled) {
      AmbientOcclusion.addToIndex(cuboid);
      AmbientOcclusion.invalidateForCuboid(cuboid);
      this._impostorAODirty = true;
    }

    // Update face occlusion for this cuboid and its neighbors
    if (FaceOcclusion.enabled) {
      FaceOcclusion.addToIndex(cuboid);
      FaceOcclusion.updateCuboid(cuboid);
      FaceOcclusion.updateNeighbors(cuboid);
    }

    // Invalidate character collision index
    if (this.collisionSystem) {
      this.collisionSystem.invalidate();
    }

    // Validate all impostors against the updated geometry
    this.validateAllImpostors();

    // Notify geometry change for NavGraph rebuild
    this._notifyGeometryChanged();

    // Notify network sync
    if (this.onMutation) {
      this.onMutation({
        op: 'addCuboid',
        data: { x, y, z, width, height, depth, materials: this._serializeMaterials(cuboid) }
      });
    }

    return cuboid;
  }

  /**
   * Add multiple cuboids in batch mode with deferred expensive operations.
   * Much faster than calling addCuboid() in a loop because:
   * - AO invalidation is done once for the bounding box of all cuboids
   * - FaceOcclusion neighbor updates are batched
   * - Collision system invalidation happens once
   * - Impostor validation and geometry change notification are skipped (caller controls)
   * @param {Array<Object>} defs - Array of {x, y, z, width, height, depth, materials, role}
   * @returns {Array<Cuboid>} The created cuboids
   */
  addCuboidBatch(defs) {
    if (!defs || defs.length === 0) return [];

    const cuboids = [];
    const fragment = document.createDocumentFragment();

    // Phase 1: Create all cuboids and add to spatial indices (cheap inserts)
    for (const def of defs) {
      const options = {
        x: def.x, y: def.y, z: def.z,
        width: def.width, height: def.height, depth: def.depth,
        cubeSize: this.cubeSize,
        materials: def.materials
      };
      const cuboid = new Cuboid(options);
      this.cuboids.set(cuboid.getKey(), cuboid);
      fragment.appendChild(cuboid.element);

      // Add to AO spatial index (skip in dungeon precalc mode - grid already has all geometry)
      if (AmbientOcclusion.enabled && !AmbientOcclusion._dungeonPrecalcMode) {
        AmbientOcclusion.addToIndex(cuboid);
      }

      // Add to FaceOcclusion spatial index (cheap insert, no neighbor updates yet)
      if (FaceOcclusion.enabled) {
        FaceOcclusion.addToIndex(cuboid);
      }

      cuboids.push(cuboid);
    }

    // Append all elements to DOM in one operation
    this.element.appendChild(fragment);
    this.fragmentsNeedClearing = true;

    // Phase 2: Deferred expensive operations (once for entire batch)

    // AO: invalidate the bounding box region of all added cuboids
    // (skipped in dungeon precalc mode - invalidateForCuboid returns early)
    if (AmbientOcclusion.enabled) {
      for (const cuboid of cuboids) {
        AmbientOcclusion.invalidateForCuboid(cuboid);
      }
      this._impostorAODirty = true;
    }

    // FaceOcclusion: update each added cuboid and collect unique neighbors
    if (FaceOcclusion.enabled) {
      const updatedNeighbors = new Set();
      for (const cuboid of cuboids) {
        FaceOcclusion.updateCuboid(cuboid);
        const neighbors = FaceOcclusion.getNeighbors(cuboid);
        for (const n of neighbors) {
          const nKey = n.getKey();
          if (!updatedNeighbors.has(nKey)) {
            updatedNeighbors.add(nKey);
            FaceOcclusion.updateCuboid(n);
          }
        }
      }
    }

    // Collision: invalidate once
    if (this.collisionSystem) {
      this.collisionSystem.invalidate();
    }

    // Skip validateAllImpostors() and _notifyGeometryChanged() —
    // caller is responsible (dungeon mode doesn't need impostor validation
    // or navmesh rebuild during streaming)

    return cuboids;
  }

  /**
   * Remove multiple cuboids in batch mode with deferred expensive operations.
   * @param {Array<Cuboid>} cuboids - Array of cuboid instances to remove
   */
  removeCuboidBatch(cuboids) {
    if (!cuboids || cuboids.length === 0) return;

    const allNeighbors = new Set();

    for (const cuboid of cuboids) {
      const key = cuboid.getKey();

      // AO: invalidate before removing (skip in dungeon precalc mode)
      if (AmbientOcclusion.enabled && !AmbientOcclusion._dungeonPrecalcMode) {
        AmbientOcclusion.invalidateForCuboid(cuboid);
        AmbientOcclusion.removeFromIndex(cuboid);
      }

      // FaceOcclusion: collect neighbors before removing
      if (FaceOcclusion.enabled) {
        const neighbors = FaceOcclusion.getNeighbors(cuboid);
        for (const n of neighbors) {
          allNeighbors.add(n);
        }
        FaceOcclusion.removeFromIndex(cuboid);
      }

      // Clean up KD tree fragments
      this.kdTree.invalidateCuboid(key);

      // Clean up audio sources
      this._cleanupCuboidAudioSources(cuboid);

      // Remove from DOM
      if (cuboid.element && cuboid.element.parentNode) {
        cuboid.element.parentNode.removeChild(cuboid.element);
      }
      this.cuboids.delete(key);
    }

    this.fragmentsNeedClearing = true;

    // FaceOcclusion: update all collected neighbors once
    if (FaceOcclusion.enabled) {
      for (const neighbor of allNeighbors) {
        // Skip neighbors that were also removed in this batch
        if (this.cuboids.has(neighbor.getKey())) {
          FaceOcclusion.updateCuboid(neighbor);
        }
      }
    }

    if (AmbientOcclusion.enabled) {
      this._impostorAODirty = true;
    }

    // Collision: invalidate once
    if (this.collisionSystem) {
      this.collisionSystem.invalidate();
    }

    // Skip _notifyGeometryChanged() — caller controls
  }

  /**
   * Validate all impostors against current geometry and remove any that violate placement rules:
   *   1. Must sit on a solid surface (floor cuboid within 0.5 Y tolerance)
   *   2. Must be at least EDGE_MARGIN from the floor cuboid's edges
   *   3. Must not intersect any solid geometry (1x1x1 virtual cuboid check)
   * @returns {number} Number of impostors removed
   */
  validateAllImpostors() {
    const EDGE_MARGIN = 0.3;
    const toRemove = [];

    for (const impostor of this.impostors.values()) {
      // Skip dynamic impostors (projectiles etc.) — they don't need floor validation
      if (impostor._skipValidation) continue;

      // 1. Find the solid floor cuboid beneath this impostor
      const floor = this._findImpostorFloor(impostor);
      if (!floor) {
        toRemove.push(impostor.id);
        continue;
      }

      // 2. Check edge margin on the floor cuboid
      if (impostor.x < floor.x + EDGE_MARGIN || impostor.x > floor.x + floor.width - EDGE_MARGIN ||
          impostor.z < floor.z + EDGE_MARGIN || impostor.z > floor.z + floor.depth - EDGE_MARGIN) {
        toRemove.push(impostor.id);
        continue;
      }

      // 3. Check intersection with solid geometry
      if (this.wouldImpostorIntersect({ x: impostor.x, y: impostor.y, z: impostor.z })) {
        toRemove.push(impostor.id);
      }
    }

    for (const id of toRemove) {
      this.removeImpostor(id);
    }
    return toRemove.length;
  }

  /**
   * Find the solid cuboid whose top surface is closest to the impostor's base Y.
   * Skips noCollision cuboids (water, gas) since impostors can only sit on solid surfaces.
   * @param {Impostor} impostor
   * @returns {Cuboid|null} The floor cuboid or null
   */
  _findImpostorFloor(impostor) {
    let bestCuboid = null;
    let bestDistance = Infinity;

    for (const cuboid of this.cuboids.values()) {
      if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;

      const topY = cuboid.y + cuboid.height;
      if (impostor.x < cuboid.x || impostor.x >= cuboid.x + cuboid.width) continue;
      if (impostor.z < cuboid.z || impostor.z >= cuboid.z + cuboid.depth) continue;

      const distance = Math.abs(impostor.y - topY);
      if (distance < bestDistance) {
        bestCuboid = cuboid;
        bestDistance = distance;
      }
    }

    return bestDistance <= 0.5 ? bestCuboid : null;
  }

  /**
   * Remove a cuboid from the world
   * @param {number} x - X coordinate (horizontal position)
   * @param {number} y - Y coordinate (height position)
   * @param {number} z - Z coordinate (depth position)
   * @param {number} width - Width (X-axis)
   * @param {number} height - Height (Y-axis)
   * @param {number} depth - Depth (Z-axis)
   * @return {boolean} True if removed, false if not found
   */
  removeCuboid(x, y, z, width = 1, height = 1, depth = 1) {
    const key = `${x},${y},${z},${width},${height},${depth}`;
    const cuboid = this.cuboids.get(key);

    if (cuboid) {
      // Update AO voxel grid - invalidate BEFORE removing so we have cuboid data
      if (AmbientOcclusion.enabled) {
        AmbientOcclusion.invalidateForCuboid(cuboid);
        AmbientOcclusion.removeFromIndex(cuboid);
        this._impostorAODirty = true;
      }

      // Get neighbors BEFORE removing from spatial index (they may need face updates)
      let neighbors = null;
      if (FaceOcclusion.enabled) {
        neighbors = FaceOcclusion.getNeighbors(cuboid);
        FaceOcclusion.removeFromIndex(cuboid);
      }

      // Clean up any fragments from the K-D tree
      this.kdTree.invalidateCuboid(key);

      // Clean up audio sources for video materials
      this._cleanupCuboidAudioSources(cuboid);

      // Remove element from DOM - use remove() which is safe even if not attached
      // (Split cuboids have their original element detached after render)
      if (cuboid.element && cuboid.element.parentNode) {
        cuboid.element.parentNode.removeChild(cuboid.element);
      }
      this.cuboids.delete(key);

      // Flag fragments for clearing since scene changed
      this.fragmentsNeedClearing = true;

      // Update neighbors' face occlusion (their hidden faces may now be visible)
      if (FaceOcclusion.enabled && neighbors) {
        for (const neighbor of neighbors) {
          FaceOcclusion.updateCuboid(neighbor);
        }
      }

      // Invalidate character collision index
      if (this.collisionSystem) {
        this.collisionSystem.invalidate();
      }

      // Notify geometry change for NavGraph rebuild
      this._notifyGeometryChanged();

      // Notify network sync
      if (this.onMutation) {
        this.onMutation({
          op: 'removeCuboid',
          data: { x, y, z, width, height, depth }
        });
      }

      return true;
    }

    return false;
  }

  /**
   * Update materials for a specific cuboid
   * @param {number} x - X coordinate (horizontal position)
   * @param {number} y - Y coordinate (height position)
   * @param {number} z - Z coordinate (depth position)
   * @param {number} width - Width (X-axis)
   * @param {number} height - Height (Y-axis)
   * @param {number} depth - Depth (Z-axis)
   * @param {Object} materials - Materials to update
   * @return {boolean} True if updated, false if not found
   */
  updateCuboidMaterials(x, y, z, width = 1, height = 1, depth = 1, materials) {
    const key = `${x},${y},${z},${width},${height},${depth}`;
    const cuboid = this.cuboids.get(key);

    if (cuboid) {
      cuboid.updateMaterials(materials);

      // Flag fragments for clearing - they'll inherit new materials on rebuild
      this.fragmentsNeedClearing = true;

      // Notify network sync
      if (this.onMutation) {
        this.onMutation({
          op: 'updateMaterials',
          data: { x, y, z, width, height, depth, materials: this._serializeMaterials(cuboid) }
        });
      }

      return true;
    }

    return false;
  }

  /**
   * Find a cuboid by its DOM element
   * @param {HTMLElement} element - The cuboid's DOM element
   * @return {Cuboid|null} The cuboid or null if not found
   */
  findCuboidByElement(element) {
    // First check main cuboids
    for (const cuboid of this.cuboids.values()) {
      if (cuboid.element === element) {
        return cuboid;
      }
    }

    // Check fragments in kdTree
    for (const fragments of this.kdTree.fragments.values()) {
      for (const fragment of fragments) {
        if (fragment.element === element) {
          // Return the original parent cuboid for editing
          if (fragment.parentKey) {
            const parentCuboid = this.cuboids.get(fragment.parentKey);
            if (parentCuboid) {
              return parentCuboid;
            }
            Debug.warn('World', `findCuboidByElement: Fragment's parent (${fragment.parentKey}) not found in cuboids`);
          }
          // Fallback to returning the fragment itself
          return fragment;
        }
      }
    }

    // Debug: element not found anywhere
    Debug.warn('World', 'findCuboidByElement: Element not found in cuboids or fragments', {
      element,
      cuboidCount: this.cuboids.size,
      fragmentGroupCount: this.kdTree.fragments.size,
      totalFragments: this.kdTree.getTotalFragmentCount()
    });

    return null;
  }

  /**
   * Re-apply group materials for a cuboid and its fragments.
   * Called after subdivision to ensure triplanar UVs are correctly calculated for fragments.
   * @param {string} cuboidKey - The cuboid's key
   */
  reapplyGroupMaterialsForCuboid(cuboidKey) {
    if (!this.materialGroupManager) return;

    // Find all groups that have faces from this cuboid
    const affectedGroups = new Set();
    for (const [groupId, group] of this.materialGroupManager.groups) {
      for (const face of group.faces) {
        if (face.cuboidKey === cuboidKey) {
          affectedGroups.add(groupId);
          break;
        }
      }
    }

    // Re-apply materials for affected groups (skip world update since we're at end of sorting)
    for (const groupId of affectedGroups) {
      this.materialGroupManager.applyGroupMaterial(groupId, true);
    }
  }

  /**
   * Generate terrain around a center point (placeholder for future implementation)
   * @param {number} centerX - Center X coordinate
   * @param {number} centerZ - Center Z coordinate (depth)
   */
  generateTerrain(centerX, centerZ) {
    // This will be implemented later
    Debug.log('World', `Will generate terrain around (${centerX}, ${centerZ})`);
  }

  /**
   * Update visible chunks based on camera position (placeholder for future implementation)
   */
  updateVisibleChunks() {
    // This will be implemented later
    Debug.log('World', 'Will update visible chunks based on camera position');
  }

  /**
   * Reconcile DOM children with a desired element order using minimum mutations.
   * Uses Longest Increasing Subsequence (LIS) to find elements already in correct
   * relative order — only elements NOT in the LIS need DOM moves. This preserves
   * browser compositor layers for stable elements (no detach/reattach cycle).
   *
   * Typical camera rotation changes only a few elements' depth order, so most
   * frames require 0-5 DOM moves instead of a full innerHTML='' rebuild.
   *
   * @param {Element[]} desiredOrder - Elements in their target DOM order
   * @param {Set<Element>} desiredSet - Set of elements that should remain
   * @private
   */
  _reconcileChildren(desiredOrder, desiredSet) {
    const parent = this.element;

    // Step 1: Remove stale elements not in the desired set
    const children = parent.children;
    for (let i = children.length - 1; i >= 0; i--) {
      if (!desiredSet.has(children[i])) {
        children[i].remove();
      }
    }

    // Step 2: Fast path — if current order already matches, no moves needed.
    // This is common when the sort order is stable between frames.
    const curChildren = parent.children;
    const curLen = curChildren.length;
    const desLen = desiredOrder.length;
    if (curLen === desLen) {
      let matches = true;
      for (let i = 0; i < desLen; i++) {
        if (curChildren[i] !== desiredOrder[i]) { matches = false; break; }
      }
      if (matches) return;
    }

    // Step 3: Build current position map (element → index in current DOM)
    // Reuse Map to avoid per-frame allocation
    const curPosMap = this._frameCurPosMap;
    curPosMap.clear();
    for (let i = 0; i < curLen; i++) {
      curPosMap.set(curChildren[i], i);
    }

    // Step 4: Map desired order to current positions (-1 = new element not in DOM)
    const curPositions = new Array(desLen);
    for (let i = 0; i < desLen; i++) {
      const pos = curPosMap.get(desiredOrder[i]);
      curPositions[i] = pos !== undefined ? pos : -1;
    }

    // Step 5: Find LIS of current positions (ignoring -1).
    // Elements in the LIS are already in correct relative order and don't need moves.
    const lisIndices = this._findLIS(curPositions);
    // Reuse Set to avoid per-frame allocation
    const stableSet = this._frameStableSet;
    stableSet.clear();
    for (let i = 0; i < lisIndices.length; i++) {
      stableSet.add(lisIndices[i]);
    }

    // Step 6: Move non-stable elements into position (right-to-left).
    // Processing right-to-left ensures each insertBefore reference is already
    // in its final position (either stable or previously moved).
    for (let i = desLen - 1; i >= 0; i--) {
      if (stableSet.has(i)) continue; // already in correct relative position

      const el = desiredOrder[i];
      const nextSibling = i + 1 < desLen ? desiredOrder[i + 1] : null;
      if (nextSibling && nextSibling.parentNode === parent) {
        parent.insertBefore(el, nextSibling);
      } else {
        parent.appendChild(el);
      }
    }
  }

  /**
   * Find indices of the Longest Increasing Subsequence in arr.
   * Skips entries with value -1 (new elements not in current DOM).
   * O(n log n) using patience sorting with predecessor tracking.
   *
   * @param {number[]} arr - Array of current positions
   * @returns {number[]} Indices into arr forming the LIS
   * @private
   */
  _findLIS(arr) {
    const n = arr.length;
    if (n === 0) return [];

    // tails[k] = smallest ending value of any increasing subsequence of length k+1
    const tails = [];
    // tailIdx[k] = index in arr corresponding to tails[k]
    const tailIdx = [];
    // pred[i] = index in arr of the predecessor of arr[i] in the LIS chain
    const pred = new Int32Array(n).fill(-1);

    for (let i = 0; i < n; i++) {
      const val = arr[i];
      if (val < 0) continue; // skip new elements

      // Binary search: find leftmost tail >= val
      let lo = 0, hi = tails.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (tails[mid] < val) lo = mid + 1;
        else hi = mid;
      }

      tails[lo] = val;
      tailIdx[lo] = i;
      pred[i] = lo > 0 ? tailIdx[lo - 1] : -1;
    }

    // Reconstruct LIS indices by tracing predecessors
    const lisLen = tails.length;
    if (lisLen === 0) return [];
    const result = new Array(lisLen);
    let k = tailIdx[lisLen - 1];
    for (let i = lisLen - 1; i >= 0; i--) {
      result[i] = k;
      k = pred[k];
    }
    return result;
  }

  /**
   * Apply group materials and AO to fragments before DOM insertion.
   * Called after building render fragments but before inserting into the visible DOM,
   * so fragments have correct triplanar textures when they first become visible.
   * @param {Array} sortedPositions - The sorted position array for AO application
   * @private
   */
  _applyFragmentMaterials(sortedPositions) {
    if (this.kdTree.splitCuboids.size === 0) return;

    // Suppress onMutation during fragment material refresh — fragments are ephemeral
    // rendering artifacts (K-D tree splits), not persistent world state. Broadcasting
    // them floods the data channel and can overflow the WebRTC send buffer on slow peers.
    const savedOnMutation = this.onMutation;
    const savedMgmOnMutation = this.materialGroupManager.onMutation;
    this.onMutation = null;
    this.materialGroupManager.onMutation = null;
    this.materialGroupManager.refreshAllMaterials(true);
    this.onMutation = savedOnMutation;
    this.materialGroupManager.onMutation = savedMgmOnMutation;

    // Apply AO to fragments after material refresh
    // On Android, refreshAllMaterials may modify face elements, requiring AO re-application
    if (AmbientOcclusion.enabled) {
      const isAndroid = DOMUtils.isAndroid();
      sortedPositions.forEach(pos => {
        // Skip sprites and ground effects - they don't have AO
        if (pos.isSprite || pos.isGroundEffect) return;

        // Skip original cuboids that have been split (they're hidden)
        if (!pos.isFragment) {
          const cuboidKey = pos.cuboid.getKey();
          if (this.kdTree.splitCuboids.has(cuboidKey)) {
            return;
          }
        }
        // Apply AO to cuboids that don't have overlays yet (first-time setup).
        // On Android when zoom changed, also re-apply AO since refreshMaterials() clears face styles.
        const needsAO = pos.cuboid.faceElements && (!pos.cuboid._aoOverlays || (isAndroid && this._androidAODirty));
        if (needsAO) {
          // On Android zoom change, clear texture tracking so styles get re-applied
          if (isAndroid && this._androidAODirty && pos.cuboid._aoOverlays) {
            Object.values(pos.cuboid._aoOverlays).forEach(overlay => {
              if (overlay) overlay._textureURL = null;
            });
          }
          Object.keys(pos.cuboid.faceElements).forEach(face => {
            const aoTexture = AmbientOcclusion.getTextureForFace(pos.cuboid, face, true);
            if (aoTexture) {
              pos.cuboid.applyAOTexture(face, aoTexture);
            }
          });
        }
      });
      if (isAndroid) this._androidAODirty = false;
    }
  }

  /**
   * Clear all fragments if camera state changed significantly.
   * Called at the start of sortAndRenderCuboids to ensure fragments
   * are rebuilt when viewing angle changes.
   */
  clearFragmentsIfNeeded() {
    const currentQuadrant = this.camera.getQuadrant();

    if (this.fragmentsNeedClearing || currentQuadrant !== this.lastCameraQuadrant) {
      this._cleanupSharedVideoStreams();

      this.kdTree.clear();
      this._spriteTriggeredSplits.clear();

      this.showAllOriginals();

      this.fragmentsNeedClearing = false;
    }

    this.lastCameraQuadrant = currentQuadrant;
  }

  /**
   * Clean up video-related resources before clearing fragments.
   * Fragment video cleanup is handled by KDTree._cleanupFragmentVideos().
   * This method is kept as a hook for any future parent-side cleanup.
   */
  _cleanupSharedVideoStreams() {
    // Fragment videos are cleaned up by KDTree.clear() via _cleanupFragmentVideos().
    // No parent-side cleanup needed since fragments use independent src-based videos.
  }

  /**
   * Clean up persistent audio source videos for a cuboid's materials.
   * Called when a cuboid is removed to prevent orphaned audio elements.
   * @param {Cuboid} cuboid - The cuboid being removed
   */
  _cleanupCuboidAudioSources(cuboid) {
    if (!cuboid.materials) return;
    const cuboidKey = cuboid.getKey();
    const groupManager = this.materialGroupManager;
    for (const [face, material] of Object.entries(cuboid.materials)) {
      if (material && material._cleanupAudioSource) {
        // Skip group materials — their audio source is shared across the group
        // and will be cleaned up when the group itself is deleted/cleared
        const faceGroup = groupManager?.getGroupForFace(cuboidKey, face);
        if (faceGroup && faceGroup.material === material) continue;
        material._cleanupAudioSource();
      }
    }
  }

  /**
   * Restore visibility of all original cuboids.
   * Called after clearing fragments to ensure originals are visible again.
   */
  showAllOriginals() {
    for (const cuboid of this.cuboids.values()) {
      if (cuboid.element) {
        cuboid.element.style.display = '';
        cuboid.element.style.visibility = ''; // Reset visibility (may have been hidden for webpage culling)
        cuboid.resumeVideos();
      }
    }
  }

  /**
   * Calculate combined above-water clip for multiple water volumes.
   * For single volume, uses proper edge/corner clip paths from WaterIntersectionManager.
   * For multiple volumes, uses simplified horizontal clip at highest water line.
   * @param {Object[]} clipInfos - Array of clip info objects from calculateClipInfoMultiple
   * @param {WaterIntersectionManager} waterManager - The water intersection manager
   * @returns {string} CSS clip-path polygon for above-water portion
   */
  calculateCombinedAboveWaterClip(clipInfos, waterManager, excludeBelowPercent = null) {
    // Single volume: use the proper clip path (handles edges, corners, L-shaped, U-shaped, etc.)
    if (clipInfos.length === 1) {
      if (excludeBelowPercent !== null && excludeBelowPercent < 99) {
        // Strip below-band from the clip by setting clipBottomPercent=100
        return waterManager.getAboveWaterClipPath({ ...clipInfos[0], clipBottomPercent: 100 });
      }
      return waterManager.getAboveWaterClipPath(clipInfos[0]);
    }

    // Multiple volumes: use actual submerged polygon shapes (not rectangle approximations).
    // Each volume's submerged clip path is already computed correctly by
    // WaterIntersectionManager, handling edge clips, corners, etc.
    // We punch each submerged polygon out of the full sprite rectangle as
    // counter-clockwise holes using the nonzero winding rule.
    const submergedPolygons = [];
    for (const c of clipInfos) {
      const clipPath = c.submergedClipPath || waterManager.getSubmergedClipPath(c);
      const points = this._parsePolygonPoints(clipPath);
      if (points && points.length >= 3) {
        submergedPolygons.push(points);
      }
    }

    if (submergedPolygons.length === 0) {
      return 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)';
    }

    // Build combined above-water clip using nonzero winding rule:
    // Full sprite rectangle (clockwise, winding +1) with each submerged
    // polygon as a counter-clockwise hole (winding -1). Connected via
    // zero-width bridges through anchor (0% 0%).
    // Inside sprite but outside holes: winding = +1 → visible
    // Inside sprite AND inside hole: winding = +1 - 1 = 0 → hidden
    const anchor = '0% 0%';
    const parts = [anchor];

    // Full sprite outer boundary (clockwise)
    parts.push('0% 0%', '100% 0%', '100% 100%', '0% 100%', '0% 0%');
    parts.push(anchor);

    // Each submerged polygon as a counter-clockwise hole
    for (const poly of submergedPolygons) {
      // Reverse winding order (clockwise → counter-clockwise = hole)
      for (let i = poly.length - 1; i >= 0; i--) {
        parts.push(`${poly[i].x}% ${poly[i].y}%`);
      }
      // Close the hole back to its first point (which is poly[last] after reversal)
      parts.push(`${poly[poly.length - 1].x}% ${poly[poly.length - 1].y}%`);
      parts.push(anchor);
    }

    // Optionally punch out the below-band region (for floating water: below-water
    // portion is a separate fragment that sorts behind the water cuboid)
    if (excludeBelowPercent !== null && excludeBelowPercent < 99) {
      const btm = excludeBelowPercent.toFixed(2);
      // Counter-clockwise hole for [btm%, 100%] region
      parts.push(`0% ${btm}%`, `0% 100%`, `100% 100%`, `100% ${btm}%`);
      parts.push(`0% ${btm}%`);
      parts.push(anchor);
    }

    return `polygon(${parts.join(', ')})`;
  }

  /**
   * Parse a CSS polygon() clip-path string into an array of {x, y} points.
   * @param {string} clipPath - e.g. "polygon(0% 50%, 100% 50%, 100% 100%, 0% 100%)"
   * @returns {Array<{x: number, y: number}>|null} Points array or null if invalid
   */
  _parsePolygonPoints(clipPath) {
    if (!clipPath || clipPath === 'none') return null;
    const match = clipPath.match(/polygon\((.+)\)/);
    if (!match) return null;

    const points = [];
    for (const pair of match[1].split(',')) {
      const parts = pair.trim().split(/\s+/);
      if (parts.length >= 2) {
        const x = parseFloat(parts[0]);
        const y = parseFloat(parts[1]);
        if (!isNaN(x) && !isNaN(y)) {
          points.push({ x, y });
        }
      }
    }
    return points.length >= 3 ? points : null;
  }

  /**
   * Update the world
   * @param {number} deltaTime - Time since last frame in seconds (optional, for characters)
   */
  update(deltaTime = 0) {
    // Update character entities (position, animation, etc.)
    let charactersMoving = false;
    if (deltaTime > 0 && this.characters.size > 0) {
      charactersMoving = this.updateCharacters(deltaTime);
    }

    // Check if camera state has changed
    this.camera.checkForChanges();

    // Track zoom changes for Android AO refresh (refreshMaterials clears face styles)
    if (this.camera.zoomLevel !== this._lastAOZoom) {
      this._androidAODirty = true;
      this._lastAOZoom = this.camera.zoomLevel;
    }

    // Determine what kind of update is needed:
    // - Full sort: geometry changed (fragments, dirty cuboids)
    // - Rate-limited sort: rotation or movement changed (affects depth order)
    // - Transform only: position/zoom/skew changed (depth order unchanged)
    // - Nothing: completely static scene

    // Immediate sort triggers: geometry changes need the full SAT sort right away.
    const needsImmediateSort =
      this.fragmentsNeedClearing ||
      this._hasDirtyCuboids();

    let didSort = false;

    if (!this.deferredSort) {
      // Legacy: sort every frame when anything changes depth order
      const needsSort = needsImmediateSort ||
        this.camera.hasRotationChanged() ||
        charactersMoving ||
        this._needsFullSort;
      if (needsSort) {
        this.sortAndRenderCuboids();
        this._needsFullSort = false;
        this._lastSortTime = performance.now();
        didSort = true;
      }
    } else {
      // Rate-limited sort triggers: rotation and movement affect depth ordering but
      // are visually indistinguishable at 10-20Hz. Between sorts, the fast
      // _updateTransformsOnly() path keeps cuboid transforms/positions current.
      // The SAT sort costs 20-40ms/frame on mobile — running it every frame during
      // rotation starves WebRTC and causes multi-second freezes.
      const needsRateLimitedSort =
        this.camera.hasRotationChanged() ||
        charactersMoving ||
        this._needsFullSort;
      const sortInterval = DOMUtils.isMobile() ? 100 : 50;

      if (needsImmediateSort) {
        this.sortAndRenderCuboids();
        this._needsFullSort = false;
        this._lastSortTime = performance.now();
        didSort = true;
      } else if (needsRateLimitedSort) {
        const now = performance.now();
        if (!this._lastSortTime || (now - this._lastSortTime) >= sortInterval) {
          this.sortAndRenderCuboids();
          this._needsFullSort = false;
          this._lastSortTime = now;
          didSort = true;
        } else {
          // Sort deferred — ensure it runs next eligible frame even if
          // rotation/movement stops before then
          this._needsFullSort = true;
        }
      }
    }

    if (!didSort) {
      if (this.camera.hasTransformChanged() || this.camera.hasRotationChanged() || this._forceRender) {
        // Fast path: skip SAT sort, just update transforms
        this._updateTransformsOnly();
      }
    }

    // Clear flags after any render
    if (didSort || this.camera.hasTransformChanged() || this.camera.hasRotationChanged() || this._forceRender) {
      this.camera.clearChangeFlag();
      this._forceRender = false;
    }
  }

  /**
   * Fast path: update only cuboid transforms without re-sorting.
   * Used when camera position/zoom/skew changes but rotation stays the same.
   * Re-evaluates frustum culling so cuboids entering/leaving the viewport
   * are correctly shown/hidden (the full sort's innerHTML='' removes culled
   * elements from the DOM, so we must re-insert them when they become visible).
   * @private
   */
  _updateTransformsOnly() {
    // Compute frustum culling parameters (same as sortAndRenderCuboids)
    const rotation = this.camera.getCachedRotation();
    const cosAngle = rotation.cos;
    const sinAngle = rotation.sin;
    const yScale = Transform.calculateYScale(this.camera.skewAngle);
    const cubeSize = this.cubeSize;

    const camScreenX = this.camera.position.x * cosAngle - this.camera.position.z * sinAngle;
    const camScreenY = (this.camera.position.x * sinAngle + this.camera.position.z * cosAngle) * yScale - cubeSize / 2 - this.camera.position.y;
    const viewport = this.camera.getViewportBounds();
    const halfWidth = viewport.halfWidth / this.camera.zoomLevel;
    const halfHeight = viewport.halfHeight / this.camera.zoomLevel;
    const MARGIN = 100; // pre-zoom pixels buffer to prevent popping

    let needsReinsert = false;

    // Update cuboid transforms with frustum culling
    const renderables = this.kdTree.getRenderables(this.cuboids);
    for (const cuboid of renderables) {
      const screenBounds = ProjectionUtils.calculateScreenBounds(cuboid, cosAngle, sinAngle, yScale, cubeSize);
      const isCulled = ProjectionUtils.isOutsideFrustum(
        screenBounds, camScreenX, camScreenY, halfWidth, halfHeight, MARGIN
      );

      if (isCulled) {
        if (cuboid.element) {
          // Webpage cuboids use visibility:hidden (not display:none) to preserve
          // iframe state — display:none can cause some browsers to unload iframe content.
          if (cuboid.hasWebpageMaterial?.()) {
            cuboid.element.style.visibility = 'hidden';
          } else {
            cuboid.element.style.display = 'none';
          }
        }
      } else {
        if (cuboid.element) {
          if (!cuboid.element.parentNode) {
            // Was removed from DOM by previous full sort's innerHTML=''
            this.element.appendChild(cuboid.element);
            needsReinsert = true;
          }
          cuboid.element.style.display = '';
          cuboid.element.style.visibility = '';
        }
        cuboid.update(this.camera);
      }
    }

    // Update character sprites — re-insert if removed from DOM by previous full sort
    for (const character of this.characters.values()) {
      if (character.sprite) {
        character.sprite.update(this.camera);
        const el = character.sprite.element;
        if (el) {
          if (!el.parentNode) {
            this.element.appendChild(el);
            el.style.display = '';
            needsReinsert = true;
          } else if (el.style.display === 'none') {
            el.style.display = '';
          }
        }
      }
    }

    // Update ground effects — re-insert if removed from DOM (only if visible)
    for (const effect of this.groundEffects.values()) {
      if (!effect.visible) continue;
      effect.updateTransform(this.camera);
      const el = effect.element;
      if (el && !el.parentNode) {
        this.element.appendChild(el);
        el.style.display = '';
        needsReinsert = true;
      }
    }

    // Update impostor positions — re-insert if removed from DOM
    for (const impostor of this.impostors.values()) {
      // Skip hidden vegetation — don't re-insert elements that the sort excluded
      if (!this._vegetationVisible && impostor._isVegetation) continue;
      impostor.update(this.camera);
      const el = impostor.element;
      if (el && !el.parentNode) {
        this.element.appendChild(el);
        el.style.display = '';
        needsReinsert = true;
      }
    }

    // If elements were reinserted into the DOM, trigger a full sort next frame
    // to restore correct depth ordering
    if (needsReinsert) {
      this._needsFullSort = true;
    }

    // Resample impostor AO as new AO data becomes available
    if (this._impostorAODirty && this.impostors.size > 0) {
      this._impostorAODirty = false;
      this.resampleImpostorAO();
    }

    // Check if zoom changed and refresh material group styles.
    // On Android, cuboid.update() calls refreshMaterials() which skips group-managed faces.
    // This ensures triplanar group materials are re-applied with the new zoom level.
    this.materialGroupManager.checkZoomAndRefresh(this.camera.zoomLevel);
  }
  
  /**
   * Sort and render cuboids in the correct order using separating axis test
   * @param {number} subdivisionDepth - Recursion depth for subdivision (internal use)
   * @param {number} startTime - Performance timestamp for time budget (internal use)
   */
  sortAndRenderCuboids(subdivisionDepth = 0, startTime = null) {
    const MAX_SUBDIVISION_PASSES = 3; // Limit recursive passes to prevent lag
    const TIME_BUDGET_MS = 50; // Maximum time budget for entire sort pipeline

    // Track start time for time budget
    if (startTime === null) {
      startTime = performance.now();
    }

    // At depth 0, hide container to prevent flash during subdivision
    if (subdivisionDepth === 0) {
      this.element.style.visibility = 'hidden';
      this.clearFragmentsIfNeeded();

      // Invalidate sprite-triggered fragments (sprite moved, old split boundaries are stale)
      if (this._spriteTriggeredSplits.size > 0) {
        for (const parentKey of this._spriteTriggeredSplits) {
          const cuboid = this.cuboids.get(parentKey);
          this.kdTree.invalidateCuboid(parentKey);
          this.kdTree.accumulatedSplits.delete(parentKey);
          if (cuboid) {
            this.kdTree.showOriginal(cuboid);
          }
        }
        this._spriteTriggeredSplits.clear();
      }

      // Reset warning state for clean logging this render pass
      this.kdTree.resetWarningState();
      // Reset ID counter for this sort pass
      this._sortIdCounter = 0;
      // Clear submerged attachments from previous frame (reuse map, clear arrays)
      for (const arr of this._submergedAttachments.values()) {
        arr.length = 0;
      }
      this._submergedAttachments.clear();
    }

    // Time budget check - if we've spent too long, skip subdivision
    const elapsed = performance.now() - startTime;
    if (elapsed > TIME_BUDGET_MS && subdivisionDepth > 0) {
      Debug.log('DepthCycles', `Time budget exceeded (${elapsed.toFixed(1)}ms), skipping further subdivision`);
      // Fall through to render with current state
      subdivisionDepth = MAX_SUBDIVISION_PASSES; // Force skip of further recursion
    }

    // Get all renderable items (original cuboids + fragments from k-d tree)
    const renderables = this.kdTree.getRenderables(this.cuboids);

    // Build an array of cuboid info with bounds (direct for-loop to avoid .map() closure overhead)
    // Reuse array to avoid per-frame allocation (GC pressure over long sessions)
    const positions = this._framePositions;
    positions.length = 0;
    for (let i = 0; i < renderables.length; i++) {
      const cuboid = renderables[i];
      positions.push({
        cuboid,
        x: cuboid.x,
        y: cuboid.y,
        z: cuboid.z,
        width: cuboid.width,
        height: cuboid.height,
        depth: cuboid.depth,
        minX: cuboid.x,
        maxX: cuboid.x + cuboid.width,
        minY: cuboid.y,
        maxY: cuboid.y + cuboid.height,
        minZ: cuboid.z,
        maxZ: cuboid.z + cuboid.depth,
        id: ++this._sortIdCounter,
        isFragment: cuboid.isFragment || false,
        isSprite: false,
        parentKey: cuboid.parentKey || null
      });
    }

    // Pre-compute camera rotation for depth calculations (used by attachment sorting)
    const _camRot = this.camera.getCachedRotation();
    const _sinAngle = _camRot.sin;
    const _cosAngle = _camRot.cos;
    const _viewingFromBelow = ProjectionUtils.isViewingFromBelow(this.camera.skewAngle);

    // Add sprite positions as virtual cuboids for SAT-based sorting
    // Sprites get proper occlusion testing but don't trigger K-D tree subdivision
    for (const character of this.characters.values()) {
      if (character.sprite) {
        const halfWidth = character.width / 2;
        const halfDepth = character.depth / 2;

        // Check for water intersection and apply clipping (supports multiple water volumes)
        const waterClipInfos = this.waterIntersectionManager.calculateClipInfoMultiple(character, this.camera);

        // Find ALL AABB-intersecting water cuboids (includes corner-touch cuboids
        // that produce no clip bands but still need forced dependency ordering).
        const allAABBWaterCuboids = this.waterIntersectionManager.findIntersectingWaterCuboids(character);

        // If character is in water, add submerged sprite fragments for each volume
        if (waterClipInfos && waterClipInfos.length > 0) {
          // Calculate and store clip paths for each volume
          for (const clipInfo of waterClipInfos) {
            clipInfo.submergedClipPath = this.waterIntersectionManager.getSubmergedClipPath(clipInfo);
          }

          // Check if there's actual below-water space (floating water above character feet).
          // Use world coordinates: only create below-water fragment when the lowest water
          // volume bottom is above the character's feet. This correctly handles stacked
          // cuboids where clipBottomPercent < 100 for the top volume but the bottom volume
          // covers that region.
          const allClipWaterCuboids = waterClipInfos.flatMap(c => c.waterCuboids);
          const lowestWaterBottomY = Math.min(...allClipWaterCuboids.map(c => c.y));
          const hasBelowWaterBand = lowestWaterBottomY > character.y + 0.01;

          let mainSpriteClip;
          if (hasBelowWaterBand) {
            // Generate above-water clip WITHOUT the below-band portion.
            // The below-band (legs below floating water) will be a separate fragment
            // that sorts behind the water cuboid.
            const belowBandPercent = ((character.y + character.height - lowestWaterBottomY) / character.height) * 100;
            mainSpriteClip = this.calculateCombinedAboveWaterClip(
              waterClipInfos, this.waterIntersectionManager, belowBandPercent
            );

            // Create below-water fragment
            const btm = belowBandPercent.toFixed(2);
            const belowWaterClip = `polygon(0% ${btm}%, 100% ${btm}%, 100% 100%, 0% 100%)`;
            character.sprite.syncBelowWaterFragment(belowWaterClip);
            const belowEl = character.sprite.getBelowWaterElement();
            if (!belowEl.parentElement) {
              this.element.appendChild(belowEl);
            }
            belowEl.style.display = '';
          } else {
            // No below-band: use full above-water clip (preserves edge clips)
            mainSpriteClip = this.calculateCombinedAboveWaterClip(waterClipInfos, this.waterIntersectionManager);
            character.sprite.clearBelowWaterFragment();
          }

          character.sprite.applyMultiVolumeWaterClip(mainSpriteClip, waterClipInfos);

          // SUBMERGED FRAGMENTS: Don't add to SAT sorting (causes cycles).
          // Instead, register them as "attachments" to their water cuboids.
          // They'll be rendered just before their water cuboid in the render phase.
          // Compute depth for intra-cuboid sorting of multiple submerged sprites
          const _charHalfW = character.width / 2;
          const _charHalfD = character.depth / 2;
          const _charFarX = _sinAngle > 0 ? character.x - _charHalfW : character.x + _charHalfW;
          const _charFarZ = _cosAngle > 0 ? character.z + _charHalfD : character.z - _charHalfD;
          const _charFarY = _viewingFromBelow ? character.y + character.sortHeight : character.y;
          const charDepth = -_charFarX * _sinAngle + _charFarZ * _cosAngle + _charFarY * 0.001 * (_viewingFromBelow ? 1 : -1);

          for (const frag of character.sprite.getSubmergedFragments()) {
            const clipInfo = frag.clipInfo;

            // Attach fragment to world if needed
            if (!frag.element.parentElement) {
              this.element.appendChild(frag.element);
            }
            frag.element.style.display = '';

            // Register this fragment as an attachment to each water cuboid in this volume
            for (const waterCuboid of clipInfo.waterCuboids) {
              const key = waterCuboid.getKey();
              if (!this._submergedAttachments.has(key)) {
                this._submergedAttachments.set(key, []);
              }
              const attachments = this._submergedAttachments.get(key);
              if (!attachments.find(a => a.element === frag.element)) {
                attachments.push({ element: frag.element, depth: charDepth });
              }
            }
          }

          const allWaterCuboids = waterClipInfos.flatMap(c => c.waterCuboids);

          // All clipped water cuboids in frontKeys: the main sprite (above-water clip)
          // must render in front of water so the clip-path (surface cuts + wall edges)
          // is visible. Submerged fragments (attachments) handle the behind-water portions.
          const clippedKeySet = new Set(allWaterCuboids.map(c => c.getKey()));
          const frontKeys = new Set(clippedKeySet);
          const behindKeys = new Set();

          for (const cuboid of allAABBWaterCuboids) {
            const cKey = cuboid.getKey();
            if (clippedKeySet.has(cKey)) continue; // already in frontKeys
            const sprSortMaxY = character.y + character.sortHeight;
            const cubMinY = cuboid.y;
            const cubMaxY = cuboid.y + cuboid.height;
            if (sprSortMaxY <= cubMinY) {
              ((_viewingFromBelow) ? frontKeys : behindKeys).add(cKey);
            } else if (character.y >= cubMaxY) {
              ((_viewingFromBelow) ? behindKeys : frontKeys).add(cKey);
            } else {
              const sprFarX = _sinAngle > 0 ? (character.x - halfWidth) : (character.x + halfWidth);
              const sprFarZ = _cosAngle > 0 ? (character.z + halfDepth) : (character.z - halfDepth);
              const cubFarX = _sinAngle > 0 ? cuboid.x : (cuboid.x + cuboid.width);
              const cubFarZ = _cosAngle > 0 ? (cuboid.z + cuboid.depth) : cuboid.z;
              const sprDepth = -sprFarX * _sinAngle + sprFarZ * _cosAngle;
              const cubDepth = -cubFarX * _sinAngle + cubFarZ * _cosAngle;
              if (sprDepth < cubDepth - 0.0001) {
                frontKeys.add(cKey);
              } else {
                behindKeys.add(cKey);
              }
            }
          }

          positions.push({
            character,
            element: character.sprite.element,
            x: character.x - halfWidth,
            y: character.y,
            z: character.z - halfDepth,
            width: character.width,
            height: character.sortHeight,
            depth: character.depth,
            minX: character.x - halfWidth,
            maxX: character.x + halfWidth,
            minY: character.y,
            maxY: character.y + character.sortHeight,
            // Visual Y bounds for screen overlap detection (full sprite height)
            visualMinY: character.y,
            visualMaxY: character.y + character.height,
            minZ: character.z - halfDepth,
            maxZ: character.z + halfDepth,
            id: `sprite-${character.id}`,
            isFragment: false,
            isSprite: true,
            isInWater: true,
            waterCuboids: allWaterCuboids,
            waterCuboidKeys: frontKeys.size > 0 ? frontKeys : undefined,
            waterCuboidBehindKeys: behindKeys.size > 0 ? behindKeys : undefined,
            parentKey: `sprite-${character.id}`
          });

          // Below-water fragment: sorts behind water cuboids that are above the sprite
          // (or in front when viewing from below). Handles flying/floating water where
          // the legs below should render on the far side of the water from the camera.
          if (hasBelowWaterBand) {
            const belowFrontKeys = new Set();
            const belowBehindKeys = new Set();
            for (const cuboid of allAABBWaterCuboids) {
              const cKey = cuboid.getKey();
              // The below-water fragment covers y=[character.y, lowestWaterBottomY].
              // Any water cuboid whose bottom >= lowestWaterBottomY is above this
              // fragment and needs a forced dependency for correct sorting.
              if (cuboid.y >= lowestWaterBottomY - 0.01) {
                if (_viewingFromBelow) {
                  belowFrontKeys.add(cKey); // below = closer when looking up
                } else {
                  belowBehindKeys.add(cKey); // below = farther when looking down
                }
              }
            }
            if (belowFrontKeys.size > 0 || belowBehindKeys.size > 0) {
              positions.push({
                character,
                element: character.sprite.getBelowWaterElement(),
                x: character.x - halfWidth,
                y: character.y,
                z: character.z - halfDepth,
                width: character.width,
                height: character.sortHeight,
                depth: character.depth,
                minX: character.x - halfWidth,
                maxX: character.x + halfWidth,
                minY: character.y,
                maxY: character.y + character.sortHeight,
                visualMinY: character.y,
                visualMaxY: character.y + character.height,
                minZ: character.z - halfDepth,
                maxZ: character.z + halfDepth,
                id: `sprite-below-${character.id}`,
                isFragment: true,
                isSprite: true,
                isInWater: true,
                waterCuboids: allWaterCuboids,
                waterCuboidKeys: belowFrontKeys.size > 0 ? belowFrontKeys : undefined,
                waterCuboidBehindKeys: belowBehindKeys.size > 0 ? belowBehindKeys : undefined,
                parentKey: `sprite-${character.id}`
              });
            }
          }
        } else {
          // No water clipping — corner-touch only. Use camera-aware side test
          // to determine if sprite should be in front or behind each cuboid.
          character.sprite.clearWaterClip();
          const hasCornerTouches = allAABBWaterCuboids.length > 0;

          let frontKeys, behindKeys;
          if (hasCornerTouches) {
            frontKeys = new Set();
            behindKeys = new Set();
            for (const cuboid of allAABBWaterCuboids) {
              const cKey = cuboid.getKey();
              const sprSortMaxY = character.y + character.sortHeight;
              const cubMinY = cuboid.y;
              const cubMaxY = cuboid.y + cuboid.height;
              if (sprSortMaxY <= cubMinY) {
                ((_viewingFromBelow) ? frontKeys : behindKeys).add(cKey);
              } else if (character.y >= cubMaxY) {
                ((_viewingFromBelow) ? behindKeys : frontKeys).add(cKey);
              } else {
                const sprFarX = _sinAngle > 0 ? (character.x - halfWidth) : (character.x + halfWidth);
                const sprFarZ = _cosAngle > 0 ? (character.z + halfDepth) : (character.z - halfDepth);
                const cubFarX = _sinAngle > 0 ? cuboid.x : (cuboid.x + cuboid.width);
                const cubFarZ = _cosAngle > 0 ? (cuboid.z + cuboid.depth) : cuboid.z;
                const sprDepth = -sprFarX * _sinAngle + sprFarZ * _cosAngle;
                const cubDepth = -cubFarX * _sinAngle + cubFarZ * _cosAngle;
                if (sprDepth < cubDepth - 0.0001) {
                  frontKeys.add(cKey);
                } else {
                  behindKeys.add(cKey);
                }
              }
            }
          }

          positions.push({
            character,
            element: character.sprite.element,
            x: character.x - halfWidth,
            y: character.y,
            z: character.z - halfDepth,
            width: character.width,
            height: character.sortHeight,
            depth: character.depth,
            minX: character.x - halfWidth,
            maxX: character.x + halfWidth,
            minY: character.y,
            maxY: character.y + character.sortHeight,
            // Visual Y bounds for screen overlap detection (full sprite height)
            visualMinY: character.y,
            visualMaxY: character.y + character.height,
            minZ: character.z - halfDepth,
            maxZ: character.z + halfDepth,
            id: `sprite-${character.id}`,
            isFragment: false,
            isSprite: true,
            isInWater: hasCornerTouches,
            waterCuboids: hasCornerTouches ? allAABBWaterCuboids : null,
            waterCuboidKeys: hasCornerTouches && frontKeys.size > 0 ? frontKeys : undefined,
            waterCuboidBehindKeys: hasCornerTouches && behindKeys.size > 0 ? behindKeys : undefined,
            parentKey: `sprite-${character.id}`
          });
        }
      }
    }

    // Add ground effects (destination markers, selection circles, etc.)
    // Ground effects are thin discs that get sorted with world objects
    for (const effect of this.groundEffects.values()) {
      if (effect.visible) {
        positions.push(effect.getSortPosition());
      }
    }

    // Add impostor positions as virtual cuboids for SAT-based sorting
    // Uses WaterIntersectionManager for proper water clipping (horizontal + wall edges)
    for (const impostor of this.impostors.values()) {
      // Skip hidden vegetation (non-dynamic impostors when vegetation display is off)
      if (!this._vegetationVisible && impostor._isVegetation) {
        impostor.element.style.display = 'none';
        continue;
      }
      impostor.update(this.camera); // Update screen position before render
      const halfW = impostor.sortWidth / 2;
      const halfD = impostor.sortDepth / 2;
      // Use sortCenterX/Z (if set) as the SAT bounding-box centre.
      // _clipSortBoxAgainstWalls and _setSortCenterOutsideWall shift the sort
      // centre away from the visual position so the box never overlaps wall
      // geometry. Without this, the SAT always uses the visual position,
      // causing explosions on back-facing wall surfaces to appear incorrectly
      // in front of the wall (the exact bug being fixed here).
      const cx = impostor.sortCenterX !== null ? impostor.sortCenterX : impostor.x;
      const cz = impostor.sortCenterZ !== null ? impostor.sortCenterZ : impostor.z;
      // SAT Y-extent: projectiles override _sortMinY/_sortHeight so their sort box
      // spans the full wall height (floor → ceiling), matching character sprites.
      // impostor.y remains the visual base so rendering is unaffected.
      const sortMinY = impostor._sortMinY !== undefined ? impostor._sortMinY : impostor.y;
      const sortH    = impostor.sortHeight; // honours _sortHeight override via getter

      // Check for water cuboid intersection
      const impWaterCuboids = this._findImpostorWaterCuboids(impostor);

      if (impWaterCuboids.length > 0) {
        // Group into connected water volumes and calculate clip info for each
        const volumes = this.waterIntersectionManager.groupConnectedWaterVolumes(impWaterCuboids);
        const volumeClipInfos = [];

        for (const volume of volumes) {
          const clipInfo = this.waterIntersectionManager.calculateImpostorClipInfoForVolume(
            impostor, this.camera, volume
          );
          if (clipInfo) {
            clipInfo.volumeId = this.waterIntersectionManager.getVolumeId(volume);
            clipInfo.submergedClipPath = this.waterIntersectionManager.getSubmergedClipPath(clipInfo);
            volumeClipInfos.push(clipInfo);
          }
        }

        if (volumeClipInfos.length > 0) {
          // Check for below-water band (flying water above impostor base).
          // Same logic as characters: create a separate below-water fragment
          // that sorts behind (or in front when viewing from below) the water.
          const allClipImpWaterCuboids = volumeClipInfos.flatMap(c => c.waterCuboids);
          const lowestImpWaterBottomY = Math.min(...allClipImpWaterCuboids.map(c => c.y));
          const impHasBelowWaterBand = lowestImpWaterBottomY > impostor.y + 0.01;

          let impAboveWaterClip;
          if (impHasBelowWaterBand) {
            // Generate above-water clip WITHOUT the below-band portion.
            const belowBandPercent = ((impostor.y + impostor.scale - lowestImpWaterBottomY) / impostor.scale) * 100;
            impAboveWaterClip = this.calculateCombinedAboveWaterClip(
              volumeClipInfos, this.waterIntersectionManager, belowBandPercent
            );

            // Create below-water fragment
            const btm = belowBandPercent.toFixed(2);
            const belowWaterClip = `polygon(0% ${btm}%, 100% ${btm}%, 100% 100%, 0% 100%)`;
            impostor.syncBelowWaterFragment(belowWaterClip);
            const belowEl = impostor.getBelowWaterElement();
            if (!belowEl.parentElement) {
              this.element.appendChild(belowEl);
            }
            belowEl.style.display = '';
          } else {
            // No below-band: use full above-water clip
            impAboveWaterClip = this.calculateCombinedAboveWaterClip(volumeClipInfos, this.waterIntersectionManager);
            impostor.clearBelowWaterFragment();
          }

          // Apply multi-volume water clip (same pattern as character sprites)
          impostor.applyMultiVolumeWaterClip(impAboveWaterClip, volumeClipInfos);

          // Register submerged fragments as attachments with depth for intra-cuboid sorting
          const impDepth = impostor.getSortDepth(_sinAngle, _cosAngle, _viewingFromBelow);
          for (const frag of impostor.getSubmergedFragments()) {
            if (!frag.element.parentElement) {
              this.element.appendChild(frag.element);
            }
            frag.element.style.display = '';

            for (const waterCuboid of frag.clipInfo.waterCuboids) {
              const wKey = waterCuboid.getKey();
              if (!this._submergedAttachments.has(wKey)) {
                this._submergedAttachments.set(wKey, []);
              }
              const attachments = this._submergedAttachments.get(wKey);
              if (!attachments.find(a => a.element === frag.element)) {
                attachments.push({ element: frag.element, depth: impDepth });
              }
            }
          }

          // All clipped water cuboids in frontKeys: the main element (above-water clip)
          // must render in front of water so clip-path is visible.
          const clippedImpKeySet = new Set(
            volumeClipInfos.flatMap(c => c.waterCuboids.map(w => w.getKey()))
          );
          const impFrontKeys = new Set(clippedImpKeySet);
          const impBehindKeys = new Set();

          for (const cuboid of impWaterCuboids) {
            const cKey = cuboid.getKey();
            if (clippedImpKeySet.has(cKey)) continue;
            const cubMinY = cuboid.y;
            const cubMaxY = cuboid.y + cuboid.height;
            const sprSortMaxY = sortMinY + sortH;
            if (sprSortMaxY <= cubMinY) {
              ((_viewingFromBelow) ? impFrontKeys : impBehindKeys).add(cKey);
            } else if (sortMinY >= cubMaxY) {
              ((_viewingFromBelow) ? impBehindKeys : impFrontKeys).add(cKey);
            } else {
              const sprFarX = _sinAngle > 0 ? (cx - halfW) : (cx + halfW);
              const sprFarZ = _cosAngle > 0 ? (cz + halfD) : (cz - halfD);
              const cubFarX = _sinAngle > 0 ? cuboid.x : (cuboid.x + cuboid.width);
              const cubFarZ = _cosAngle > 0 ? (cuboid.z + cuboid.depth) : cuboid.z;
              const sprDepth = -sprFarX * _sinAngle + sprFarZ * _cosAngle;
              const cubDepth = -cubFarX * _sinAngle + cubFarZ * _cosAngle;
              if (sprDepth < cubDepth - 0.0001) {
                impFrontKeys.add(cKey);
              } else {
                impBehindKeys.add(cKey);
              }
            }
          }

          // Visual Y bounds: cover both sort AABB and visual rendered area
          const _impVisualMinY = Math.min(sortMinY, impostor.y);
          const _impVisualMaxY = Math.max(sortMinY + sortH, impostor.y + impostor.scale);
          positions.push({
            impostor,
            element: impostor.element,
            x: cx - halfW,
            y: sortMinY,
            z: cz - halfD,
            width: impostor.sortWidth,
            height: sortH,
            depth: impostor.sortDepth,
            minX: cx - halfW,
            maxX: cx + halfW,
            minY: sortMinY,
            maxY: sortMinY + sortH,
            visualMinY: _impVisualMinY,
            visualMaxY: _impVisualMaxY,
            minZ: cz - halfD,
            maxZ: cz + halfD,
            id: `impostor-${impostor.id}`,
            isFragment: false,
            isSprite: true,
            isDynamic: !!impostor._isDynamic,
            isInWater: true,
            waterCuboids: impWaterCuboids,
            waterCuboidKeys: impFrontKeys.size > 0 ? impFrontKeys : undefined,
            waterCuboidBehindKeys: impBehindKeys.size > 0 ? impBehindKeys : undefined,
            parentKey: `impostor-${impostor.id}`
          });

          // Below-water fragment position entry (same as character below-water fragment)
          if (impHasBelowWaterBand) {
            const belowFrontKeys = new Set();
            const belowBehindKeys = new Set();
            for (const cuboid of impWaterCuboids) {
              const cKey = cuboid.getKey();
              // The below-water fragment covers y=[impostor.y, lowestImpWaterBottomY].
              // Any water cuboid whose bottom >= lowestImpWaterBottomY is above this
              // fragment and needs a forced dependency for correct sorting.
              if (cuboid.y >= lowestImpWaterBottomY - 0.01) {
                if (_viewingFromBelow) {
                  belowFrontKeys.add(cKey); // below = closer when looking up
                } else {
                  belowBehindKeys.add(cKey); // below = farther when looking down
                }
              }
            }
            if (belowFrontKeys.size > 0 || belowBehindKeys.size > 0) {
              positions.push({
                impostor,
                element: impostor.getBelowWaterElement(),
                x: cx - halfW,
                y: sortMinY,
                z: cz - halfD,
                width: impostor.sortWidth,
                height: sortH,
                depth: impostor.sortDepth,
                minX: cx - halfW,
                maxX: cx + halfW,
                minY: sortMinY,
                maxY: sortMinY + sortH,
                visualMinY: _impVisualMinY,
                visualMaxY: _impVisualMaxY,
                minZ: cz - halfD,
                maxZ: cz + halfD,
                id: `impostor-below-${impostor.id}`,
                isFragment: true,
                isSprite: true,
                isDynamic: !!impostor._isDynamic,
                isInWater: true,
                waterCuboids: impWaterCuboids,
                waterCuboidKeys: belowFrontKeys.size > 0 ? belowFrontKeys : undefined,
                waterCuboidBehindKeys: belowBehindKeys.size > 0 ? belowBehindKeys : undefined,
                parentKey: `impostor-${impostor.id}`
              });
            }
          }
        } else {
          // No valid clip info — corner-touch only, camera-aware direction.
          impostor.clearWaterClip();
          const impFrontKeys = new Set();
          const impBehindKeys = new Set();
          for (const cuboid of impWaterCuboids) {
            const cKey = cuboid.getKey();
            const cubMinY = cuboid.y;
            const cubMaxY = cuboid.y + cuboid.height;
            const sprSortMaxY = sortMinY + sortH;
            if (sprSortMaxY <= cubMinY) {
              ((_viewingFromBelow) ? impFrontKeys : impBehindKeys).add(cKey);
            } else if (sortMinY >= cubMaxY) {
              ((_viewingFromBelow) ? impBehindKeys : impFrontKeys).add(cKey);
            } else {
              const sprFarX = _sinAngle > 0 ? (cx - halfW) : (cx + halfW);
              const sprFarZ = _cosAngle > 0 ? (cz + halfD) : (cz - halfD);
              const cubFarX = _sinAngle > 0 ? cuboid.x : (cuboid.x + cuboid.width);
              const cubFarZ = _cosAngle > 0 ? (cuboid.z + cuboid.depth) : cuboid.z;
              const sprDepth = -sprFarX * _sinAngle + sprFarZ * _cosAngle;
              const cubDepth = -cubFarX * _sinAngle + cubFarZ * _cosAngle;
              if (sprDepth < cubDepth - 0.0001) {
                impFrontKeys.add(cKey);
              } else {
                impBehindKeys.add(cKey);
              }
            }
          }
          // Visual Y bounds: cover both sort AABB and visual rendered area
          const _impVisualMinY2 = Math.min(sortMinY, impostor.y);
          const _impVisualMaxY2 = Math.max(sortMinY + sortH, impostor.y + impostor.scale);
          positions.push({
            impostor,
            element: impostor.element,
            x: cx - halfW,
            y: sortMinY,
            z: cz - halfD,
            width: impostor.sortWidth,
            height: sortH,
            depth: impostor.sortDepth,
            minX: cx - halfW,
            maxX: cx + halfW,
            minY: sortMinY,
            maxY: sortMinY + sortH,
            visualMinY: _impVisualMinY2,
            visualMaxY: _impVisualMaxY2,
            minZ: cz - halfD,
            maxZ: cz + halfD,
            id: `impostor-${impostor.id}`,
            isFragment: false,
            isSprite: true,
            isDynamic: !!impostor._isDynamic,
            isInWater: true,
            waterCuboids: impWaterCuboids,
            waterCuboidKeys: impFrontKeys.size > 0 ? impFrontKeys : undefined,
            waterCuboidBehindKeys: impBehindKeys.size > 0 ? impBehindKeys : undefined,
            parentKey: `impostor-${impostor.id}`
          });
        }
      } else {
        // Not in water
        impostor.clearWaterClip();
        // Visual Y bounds: cover both sort AABB and visual rendered area
        const _impVisualMinY3 = Math.min(sortMinY, impostor.y);
        const _impVisualMaxY3 = Math.max(sortMinY + sortH, impostor.y + impostor.scale);
        positions.push({
          impostor,
          element: impostor.element,
          x: cx - halfW,
          y: sortMinY,
          z: cz - halfD,
          width: impostor.sortWidth,
          height: sortH,
          depth: impostor.sortDepth,
          minX: cx - halfW,
          maxX: cx + halfW,
          minY: sortMinY,
          maxY: sortMinY + sortH,
          visualMinY: _impVisualMinY3,
          visualMaxY: _impVisualMaxY3,
          minZ: cz - halfD,
          maxZ: cz + halfD,
          id: `impostor-${impostor.id}`,
          isFragment: false,
          isSprite: true,
          isDynamic: !!impostor._isDynamic,
          isInWater: false,
          waterCuboids: null,
          parentKey: `impostor-${impostor.id}`
        });
      }
    }

    // Get camera parameters for projection using cached rotation
    const rotation = this.camera.getCachedRotation();
    const cosAngle = rotation.cos;
    const sinAngle = rotation.sin;
    // Convert skewAngle to proper projection factor using Transform utility
    const yScale = Transform.calculateYScale(this.camera.skewAngle);
    const viewingFromBelow = ProjectionUtils.isViewingFromBelow(this.camera.skewAngle);
    const cubeSize = this.cubeSize;

    // Calculate screen bounds for frustum culling (needed for ALL positions)
    // Uses O(1) analytic AABB — no 8-corner projection needed
    for (let i = 0; i < positions.length; i++) {
      positions[i].screenBounds = ProjectionUtils.calculateScreenBoundsFromBounds(positions[i], cosAngle, sinAngle, yScale, cubeSize);
    }

    // === FRUSTUM CULLING ===
    // Calculate camera's screen-space offset
    // Note: camera.position is already in world pixel coordinates (same as worldZ in Cuboid.update)
    // Do NOT negate camera.position.z - it's already in world coords where worldZ = -gameZ
    // The cubeSize/2 offset compensates for the rendering offset in Cuboid.update's effectiveCenterY
    // camera.position.y offsets for vertical camera panning (following player height)
    const camScreenX = this.camera.position.x * cosAngle - this.camera.position.z * sinAngle;
    const camScreenY = (this.camera.position.x * sinAngle + this.camera.position.z * cosAngle) * yScale - cubeSize / 2 - this.camera.position.y;

    // Get viewport bounds (in pre-zoom pixel space)
    const viewport = this.camera.getViewportBounds();
    const halfWidth = viewport.halfWidth / this.camera.zoomLevel;
    const halfHeight = viewport.halfHeight / this.camera.zoomLevel;

    // Filter to only visible positions (cuboids and sprites)
    const FRUSTUM_MARGIN = 100; // pre-zoom pixels buffer to prevent popping at viewport edges
    const visiblePositions = positions.filter(pos => {
      const isCulled = ProjectionUtils.isOutsideFrustum(
        pos.screenBounds, camScreenX, camScreenY, halfWidth, halfHeight, FRUSTUM_MARGIN
      );
      if (isCulled) {
        // Check if this is a webpage cuboid - never hide with display:none to preserve iframe state
        if (!pos.isSprite && !pos.isGroundEffect && pos.cuboid.hasWebpageMaterial?.()) {
          // Use visibility:hidden instead of display:none to preserve iframe state
          // The iframe content persists even when not visible
          pos.cuboid.element.style.visibility = 'hidden';
          // Still include in visible positions so it gets sorted and rendered
          return true;
        }
        // Hide the element - sprites/ground effects use pos.element, cuboids use pos.cuboid.element
        const element = (pos.isSprite || pos.isGroundEffect) ? pos.element : pos.cuboid.element;
        element.style.display = 'none';
      }
      return !isCulled;
    });

    // Update culling stats display
    const cullStatsEl = document.getElementById('cullStats');
    if (cullStatsEl) {
      const culledCount = positions.length - visiblePositions.length;
      cullStatsEl.textContent = `Culled: ${culledCount}/${positions.length}`;
    }

    // Performance profiling for sort pipeline
    const PERF = Debug.enabled && Debug.isCategoryEnabled('SortPerf');
    const perfT0 = PERF ? performance.now() : 0;

    // Calculate convex hulls ONLY for visible positions (lazy evaluation after frustum culling)
    // Uses O(1) analytic hull with pre-computed winding order (no sorting/atan2)
    for (let i = 0; i < visiblePositions.length; i++) {
      visiblePositions[i].convexHull = ProjectionUtils.computeIsometricHull(visiblePositions[i], cosAngle, sinAngle, yScale, cubeSize);
    }

    const perfT1 = PERF ? performance.now() : 0;

    // Build dependency graph (including all edges, even those that create cycles)
    // Reuse Map to avoid per-frame allocation (GC pressure over long sessions)
    const dependencies = this._frameDependencies;
    dependencies.clear();
    for (let i = 0; i < visiblePositions.length; i++) {
      dependencies.set(visiblePositions[i], new Set());
    }

    // Compute depth value for tie-breaking (used when cycles need resolution)
    function computeDepth(pos) {
      // Use cached value if available (pre-computed after frustum culling)
      if (pos._depth !== undefined) return pos._depth;
      const farX = sinAngle > 0 ? pos.minX : pos.maxX;
      const farZ = cosAngle > 0 ? pos.maxZ : pos.minZ;
      const farY = viewingFromBelow ? pos.maxY : pos.minY;
      const depthXZ = -farX * sinAngle + farZ * cosAngle;
      let depth = depthXZ + farY * 0.001 * (viewingFromBelow ? 1 : -1);

      // Ground effects (floor markers) should render BEHIND sprites at the same Y level
      // Give them a slight depth offset to push them "further back" in render order
      if (pos.isGroundEffect) {
        depth -= 0.5; // Render before (behind) other objects at same position
      }

      return depth;
    }

    // Pre-compute and cache depth for all visible positions
    // Avoids redundant recomputation during Kahn's algorithm and sorting
    for (let i = 0; i < visiblePositions.length; i++) {
      visiblePositions[i]._depth = computeDepth(visiblePositions[i]);
    }

    // Pre-sweep: add forced dependencies for sprite ↔ water cuboid pairs.
    // This must happen BEFORE the sweep-line because corner-touch cuboids can
    // have sub-pixel screen overlap with the sprite (tiny 3D AABB intersection)
    // causing the sweep to skip the pair entirely.
    // Build key → sort entries map for efficient lookup.
    let edgeCount = 0;
    // Reuse Map to avoid per-frame allocation
    const cuboidKeyToEntries = this._frameCuboidKeyToEntries;
    cuboidKeyToEntries.clear();
    for (const pos of visiblePositions) {
      if (pos.cuboid) {
        const key = pos.cuboid.getKey();
        if (!cuboidKeyToEntries.has(key)) cuboidKeyToEntries.set(key, []);
        cuboidKeyToEntries.get(key).push(pos);
        // Also index by parentKey for K-D tree fragments
        if (pos.parentKey) {
          if (!cuboidKeyToEntries.has(pos.parentKey)) cuboidKeyToEntries.set(pos.parentKey, []);
          cuboidKeyToEntries.get(pos.parentKey).push(pos);
        }
      }
    }
    // Add forced dependencies for sprites in water.
    // waterCuboidKeys: sprite in front (sprite renders after cuboid)
    // waterCuboidBehindKeys: sprite behind (cuboid renders after sprite)
    // Direction for corner-touch cuboids is determined by camera-aware depth test.
    for (const pos of visiblePositions) {
      if (pos.isInWater) {
        if (pos.waterCuboidKeys) {
          for (const wKey of pos.waterCuboidKeys) {
            const entries = cuboidKeyToEntries.get(wKey);
            if (entries) {
              for (const cuboidEntry of entries) {
                dependencies.get(pos).add(cuboidEntry); // sprite after cuboid
                edgeCount++;
              }
            }
          }
        }
        if (pos.waterCuboidBehindKeys) {
          for (const wKey of pos.waterCuboidBehindKeys) {
            const entries = cuboidKeyToEntries.get(wKey);
            if (entries) {
              for (const cuboidEntry of entries) {
                dependencies.get(cuboidEntry).add(pos); // cuboid after sprite
                edgeCount++;
              }
            }
          }
        }
      }
    }

    // Build full dependency graph using sweep-and-prune on screen X axis.
    // Sort by screen minX, then for each position only compare against positions
    // whose screen X interval overlaps. This is O(n log n + k) where k = overlapping
    // pairs, vs the previous O(n^2) with a hard cap of 50,000 comparisons.
    const sortedByScreenX = visiblePositions.slice().sort(
      (a, b) => a.screenBounds.minX - b.screenBounds.minX
    );

    for (let i = 0; i < sortedByScreenX.length; i++) {
      const a = sortedByScreenX[i];

      for (let j = i + 1; j < sortedByScreenX.length; j++) {
        const b = sortedByScreenX[j];

        // Prune: if b's left edge is past a's right edge, no more X overlap possible
        if (b.screenBounds.minX >= a.screenBounds.maxX) {
          break;
        }

        // Check Y overlap (screen-space AABB)
        if (a.screenBounds.maxY <= b.screenBounds.minY ||
            a.screenBounds.minY >= b.screenBounds.maxY) {
          continue;
        }

        // Fast path: skip if overlap area is sub-pixel
        const overlapX = Math.min(a.screenBounds.maxX, b.screenBounds.maxX) - b.screenBounds.minX;
        const overlapY = Math.min(a.screenBounds.maxY, b.screenBounds.maxY) - Math.max(a.screenBounds.minY, b.screenBounds.minY);
        if (overlapX < 1 || overlapY < 1) {
          continue;
        }

        // Accurate convex hull overlap test
        if (!ProjectionUtils.convexPolygonsOverlap(a.convexHull, b.convexHull)) {
          continue;
        }

        // Special case: Ground effects should render BEHIND sprites/cuboids at the same Y level
        // This ensures floor markers appear under characters standing on them
        if (a.isGroundEffect !== b.isGroundEffect) {
          const groundEffect = a.isGroundEffect ? a : b;
          const other = a.isGroundEffect ? b : a;

          // Check if they're at similar Y levels (ground effect on same surface as other's feet)
          const yTolerance = 0.5;
          if (Math.abs(groundEffect.minY - other.minY) < yTolerance) {
            // Ground effect renders first (behind), so other depends on ground effect
            dependencies.get(other).add(groundEffect);
            edgeCount++;
            continue;
          }
        }

        // Skip SAT for sprite ↔ water cuboid pairs (dependencies already
        // added in pre-sweep phase to avoid sub-pixel screen overlap filtering).
        // Check both frontKeys and behindKeys — both directions have forced deps.
        if (a.isInWater && b.cuboid) {
          const bKey = b.cuboid.getKey();
          const bPK = b.parentKey;
          if ((a.waterCuboidKeys && (a.waterCuboidKeys.has(bKey) || (bPK && a.waterCuboidKeys.has(bPK)))) ||
              (a.waterCuboidBehindKeys && (a.waterCuboidBehindKeys.has(bKey) || (bPK && a.waterCuboidBehindKeys.has(bPK))))) {
            continue;
          }
        }
        if (b.isInWater && a.cuboid) {
          const aKey = a.cuboid.getKey();
          const aPK = a.parentKey;
          if ((b.waterCuboidKeys && (b.waterCuboidKeys.has(aKey) || (aPK && b.waterCuboidKeys.has(aPK)))) ||
              (b.waterCuboidBehindKeys && (b.waterCuboidBehindKeys.has(aKey) || (aPK && b.waterCuboidBehindKeys.has(aPK))))) {
            continue;
          }
        }

        // Determine occlusion using separating axis test
        const result = determineOcclusionSAT(a, b, sinAngle, cosAngle, viewingFromBelow);

        if (result === -1) {
          // A is behind B, so B depends on A (A must be drawn first)
          dependencies.get(b).add(a);
          edgeCount++;
        } else if (result === 1) {
          // B is behind A, so A depends on B (B must be drawn first)
          dependencies.get(a).add(b);
          edgeCount++;
        }
      }
    }

    const perfT2 = PERF ? performance.now() : 0;

    // Tarjan's algorithm to find strongly connected components (cycles)
    function findSCCs(nodes, deps) {
      let index = 0;
      const indices = new Map();
      const lowlinks = new Map();
      const onStack = new Set();
      const stack = [];
      const sccs = [];

      function strongconnect(node) {
        indices.set(node, index);
        lowlinks.set(node, index);
        index++;
        stack.push(node);
        onStack.add(node);

        const nodeDeps = deps.get(node) || new Set();
        for (const dep of nodeDeps) {
          if (!indices.has(dep)) {
            strongconnect(dep);
            lowlinks.set(node, Math.min(lowlinks.get(node), lowlinks.get(dep)));
          } else if (onStack.has(dep)) {
            lowlinks.set(node, Math.min(lowlinks.get(node), indices.get(dep)));
          }
        }

        if (lowlinks.get(node) === indices.get(node)) {
          const scc = [];
          let w;
          do {
            w = stack.pop();
            onStack.delete(w);
            scc.push(w);
          } while (w !== node);
          sccs.push(scc);
        }
      }

      for (const node of nodes) {
        if (!indices.has(node)) {
          strongconnect(node);
        }
      }

      return sccs;
    }

    // Find all SCCs
    const sccs = findSCCs(visiblePositions, dependencies);

    const perfT3 = PERF ? performance.now() : 0;
    if (PERF) {
      const multiMemberSCCs = sccs.filter(s => s.length > 1);
      const largestSCC = multiMemberSCCs.reduce((max, s) => Math.max(max, s.length), 0);
      Debug.log('SortPerf', `Pass ${subdivisionDepth}: ${visiblePositions.length} visible, ${edgeCount} edges, ${multiMemberSCCs.length} cycles (largest: ${largestSCC}) | hulls: ${(perfT1 - perfT0).toFixed(1)}ms, sweep: ${(perfT2 - perfT1).toFixed(1)}ms, SCC find: ${(perfT3 - perfT2).toFixed(1)}ms`);
    }

    // Resolve cycles using full pairwise axis separation tests
    // For any pair of non-intersecting boxes, at least one axis must separate them
    // We use the axis with the LARGEST separation to determine definitive ordering
    // Track if subdivision occurred during cycle resolution
    let subdivisionOccurred = false;

    // Global split plan: collects split candidates across ALL SCCs before executing.
    // Merging across SCCs enables gridSubdivide (multi-axis splits in one pass).
    // Map: cuboidKey -> { cuboid, posRef, axisSplits: Map<axis -> Set<positions>> }
    const globalSplitPlan = new Map();

    // Reference to this World for access in nested functions
    const world = this;

    function resolveCycleWithFullSAT(scc, timeBudgetExceeded) {
      if (scc.length <= 1) return;

      const EPSILON = 0.0001;
      const DEBUG_CYCLES = Debug.enabled && Debug.isCategoryEnabled('DepthCycles');

      // SCC size cap: for very large SCCs, the O(n²) pairwise analysis is too expensive.
      // Fall back to depth sort which is O(n log n).
      const MAX_SCC_SIZE_FOR_FULL_ANALYSIS = 40;
      if (scc.length > MAX_SCC_SIZE_FOR_FULL_ANALYSIS) {
        if (DEBUG_CYCLES) {
          Debug.log('DepthCycles', `SCC size ${scc.length} exceeds cap ${MAX_SCC_SIZE_FOR_FULL_ANALYSIS}, using depth sort`);
        }
        scc.sort((a, b) => computeDepth(b) - computeDepth(a));
        return;
      }

      // Track which parent pairs we've already attempted to resolve
      const attemptedParentPairs = new Set();

      // Helper to collect split info without performing the split (for multi-split)
      const collectSplitInfo = (posA, posB) => {
        // Skip subdivision when time budget exceeded
        if (timeBudgetExceeded) return null;

        // Both sprites: can't split either
        if (posA.isSprite && posB.isSprite) return null;

        // Never subdivide walls due to dynamic impostors (projectiles move every frame).
        // Subdivision fragments are persistent; stale fragments from previous positions
        // corrupt depth ordering as the projectile moves across frame boundaries.
        if (posA.isDynamic || posB.isDynamic) return null;

        // Never subdivide cuboids with noSubdivide materials (iframes, etc.)
        // (sprites don't have .cuboid, so guard the check)
        if (!posA.isSprite && posA.cuboid?.hasNoSubdivideMaterial?.()) return null;
        if (!posB.isSprite && posB.cuboid?.hasNoSubdivideMaterial?.()) return null;

        const parentKeyA = posA.parentKey || posA.cuboid.getKey();
        const parentKeyB = posB.parentKey || posB.cuboid.getKey();

        // Skip same-parent fragment pairs
        if (parentKeyA === parentKeyB) return null;

        // Skip already-attempted parent pairs
        const pairKey = parentKeyA < parentKeyB ? `${parentKeyA}|${parentKeyB}` : `${parentKeyB}|${parentKeyA}`;
        if (attemptedParentPairs.has(pairKey)) return null;
        attemptedParentPairs.add(pairKey);

        // Check fragment limits — sprites can never be split
        const canSplitA = !posA.isSprite && !world.kdTree.isExhausted(parentKeyA) && world.kdTree.canAddFragments(parentKeyA, true);
        const canSplitB = !posB.isSprite && !world.kdTree.isExhausted(parentKeyB) && world.kdTree.canAddFragments(parentKeyB, true);
        if (!canSplitA && !canSplitB) return null;

        // Calculate split plane
        const splitPlane = ProjectionUtils.calculateSplitPlane(posA, posB, world.camera);
        if (!splitPlane) return null;

        // Determine which cuboid to split — force non-sprite side
        let effectiveSplitChoice = splitPlane.cuboidToSplit;
        if (posA.isSprite) effectiveSplitChoice = 'B';
        else if (posB.isSprite) effectiveSplitChoice = 'A';

        const suggestedCanSplit = effectiveSplitChoice === 'A' ? canSplitA : canSplitB;
        const otherCanSplit = effectiveSplitChoice === 'A' ? canSplitB : canSplitA;

        if (!suggestedCanSplit && otherCanSplit) {
          effectiveSplitChoice = effectiveSplitChoice === 'A' ? 'B' : 'A';
        }

        const posToSplit = effectiveSplitChoice === 'A' ? posA : posB;
        const cuboidToSplit = posToSplit.cuboid;
        const cuboidKey = posToSplit.parentKey || cuboidToSplit.getKey();

        // Track sprite-triggered splits for cleanup when sprites move
        if (posA.isSprite || posB.isSprite) {
          world._spriteTriggeredSplits.add(cuboidKey);
        }

        return {
          cuboidToSplit,
          cuboidKey,
          axis: splitPlane.axis,
          position: splitPlane.position,
          posRef: posToSplit
        };
      };

      // Helper: collect split candidates from edges into the global split plan
      const collectEdgesIntoGlobalPlan = (edges, maxEdges = Infinity) => {
        const edgesToExamine = maxEdges < edges.length ? edges.slice(0, maxEdges) : edges;
        for (const edge of edgesToExamine) {
          const splitInfo = collectSplitInfo(edge.from, edge.to);
          if (!splitInfo) continue;

          const { cuboidToSplit, axis, position, cuboidKey, posRef } = splitInfo;
          if (!cuboidToSplit) continue;

          const parentKey = posRef.parentKey || cuboidToSplit.getKey();
          if (!world.kdTree.canAddFragments(parentKey)) continue;

          if (!globalSplitPlan.has(cuboidKey)) {
            globalSplitPlan.set(cuboidKey, { cuboid: cuboidToSplit, posRef, axisSplits: new Map() });
          }
          const globalEntry = globalSplitPlan.get(cuboidKey);
          if (!globalEntry.axisSplits.has(axis)) {
            globalEntry.axisSplits.set(axis, new Set());
          }
          globalEntry.axisSplits.get(axis).add(position);
        }
      };

      // Helper to format cuboid position for debugging
      function formatPos(p) {
        return `[${p.minX},${p.minY},${p.minZ}]-[${p.maxX},${p.maxY},${p.maxZ}]`;
      }

      if (DEBUG_CYCLES) {
        console.group(`[DepthCycles] Resolving SCC with ${scc.length} members:`);
        scc.forEach((p, i) => Debug.log('DepthCycles', `  ${i}: ${formatPos(p)}`));
      }

      // EARLY TERMINATION: Check if SCC is dominated by same-parent fragments
      // If most members share the same parent, subdivision won't help - they'll just
      // keep creating more fragments with the same depth ambiguity
      const parentCounts = new Map();
      let exhaustedParentCount = 0;
      for (const p of scc) {
        // Skip sprites - they don't have parent keys or cuboids
        if (p.isSprite) continue;
        const parentKey = p.parentKey || p.cuboid.getKey();
        parentCounts.set(parentKey, (parentCounts.get(parentKey) || 0) + 1);
      }
      // Check how many unique parents are exhausted (hit fragment limit)
      for (const parentKey of parentCounts.keys()) {
        if (world.kdTree.isExhausted(parentKey)) {
          exhaustedParentCount++;
        }
      }
      const maxSameParent = Math.max(...parentCounts.values());
      const uniqueParents = parentCounts.size;

      // Early termination conditions:
      // 1. Dominated by fragments from same parent(s)
      // 2. All parents have hit their fragment limits
      // Don't early-terminate when sprites are in the SCC — subdivision of the cuboid side can help
      const hasSpriteInSCC = scc.some(p => p.isSprite);
      if (!hasSpriteInSCC && (maxSameParent >= scc.length * 0.6 || uniqueParents <= 2)) {
        // Dominated by fragments from same parent(s) - skip subdivision
        if (DEBUG_CYCLES) {
          Debug.log('DepthCycles', `  [Early Termination] SCC has ${maxSameParent}/${scc.length} from same parent, ${uniqueParents} unique parents - using depth sort`);
          console.groupEnd();
        }
        // Just sort by depth and return
        scc.sort((a, b) => computeDepth(b) - computeDepth(a));
        return;
      }

      if (exhaustedParentCount === uniqueParents) {
        // All parents have hit their fragment limits - no point trying to subdivide
        if (DEBUG_CYCLES) {
          Debug.log('DepthCycles', `  [Early Termination] All ${uniqueParents} parents exhausted - using depth sort`);
          console.groupEnd();
        }
        scc.sort((a, b) => computeDepth(b) - computeDepth(a));
        return;
      }

      // For each pair, determine definitive ordering based on axis separation
      // Priority: Y (if XZ overlap) > max(X sep, Z sep) > depth
      function getDefinitiveOrder(a, b, logReason = false) {
        // Calculate separation distances on each axis
        // Positive value means separated, 0 means edge-touching, negative means overlapping
        const xSepA = a.minX - b.maxX; // positive if A is to the right of B
        const xSepB = b.minX - a.maxX; // positive if B is to the right of A
        const xSep = Math.max(xSepA, xSepB, 0);

        const zSepA = a.minZ - b.maxZ; // positive if A is behind B (larger Z)
        const zSepB = b.minZ - a.maxZ; // positive if B is behind A
        const zSep = Math.max(zSepA, zSepB, 0);

        const ySepA = a.minY - b.maxY; // positive if A is above B
        const ySepB = b.minY - a.maxY; // positive if B is above A
        const ySep = Math.max(ySepA, ySepB, 0);

        // Check for strict overlap (not just touching)
        // Edge-touching (sep = 0) should be treated as separated, not overlapping
        const xOverlap = a.minX < b.maxX - EPSILON && b.minX < a.maxX - EPSILON;
        const zOverlap = a.minZ < b.maxZ - EPSILON && b.minZ < a.maxZ - EPSILON;
        const xzOverlap = xOverlap && zOverlap;

        // Check for edge-touching cases (separation exactly 0)
        const xTouchingA = Math.abs(a.minX - b.maxX) < EPSILON; // A touches B from right
        const xTouchingB = Math.abs(b.minX - a.maxX) < EPSILON; // B touches A from right
        const zTouchingA = Math.abs(a.minZ - b.maxZ) < EPSILON; // A touches B from behind
        const zTouchingB = Math.abs(b.minZ - a.maxZ) < EPSILON; // B touches A from behind
        const yTouchingA = Math.abs(a.minY - b.maxY) < EPSILON; // A touches B from above
        const yTouchingB = Math.abs(b.minY - a.maxY) < EPSILON; // B touches A from above

        // Check if X and Z ranges are essentially identical (vertically stacked cuboids)
        const xRangesIdentical = Math.abs(a.minX - b.minX) < EPSILON && Math.abs(a.maxX - b.maxX) < EPSILON;
        const zRangesIdentical = Math.abs(a.minZ - b.minZ) < EPSILON && Math.abs(a.maxZ - b.maxZ) < EPSILON;
        const verticallyStacked = xRangesIdentical && zRangesIdentical;

        const log = (axis, sep, result) => {
          if (logReason) {
            const winner = result === -1 ? 'A' : 'B';
            Debug.log('DepthCycles', `    ${formatPos(a)} vs ${formatPos(b)}: ${axis} sep=${sep.toFixed(3)} → ${winner} behind`);
          }
        };

        // If XZ overlap exists and Y separates, Y is ALWAYS dominant
        if (xzOverlap && ySep > EPSILON) {
          if (viewingFromBelow) {
            const result = ySepA > EPSILON ? -1 : 1;
            log('Y(XZ-overlap)', ySep, result);
            return result;
          } else {
            const result = ySepB > EPSILON ? -1 : 1;
            log('Y(XZ-overlap)', ySep, result);
            return result;
          }
        }

        // Handle Y edge-touching with XZ overlap (vertically stacked cuboids)
        // This is critical when cuboids have identical XZ ranges
        if (xzOverlap && (yTouchingA || yTouchingB)) {
          if (yTouchingA) {
            // A.minY = B.maxY: A is directly above B
            if (viewingFromBelow) {
              log('Y(edge-touch)', 0, -1);
              return -1; // A is behind (above, viewing from below)
            } else {
              log('Y(edge-touch)', 0, 1);
              return 1; // B is behind (below, viewing from above)
            }
          }
          if (yTouchingB) {
            // B.minY = A.maxY: B is directly above A
            if (viewingFromBelow) {
              log('Y(edge-touch)', 0, 1);
              return 1; // B is behind (above, viewing from below)
            } else {
              log('Y(edge-touch)', 0, -1);
              return -1; // A is behind (below, viewing from above)
            }
          }
        }

        // For vertically stacked cuboids with identical XZ, use Y ordering definitively
        // This prevents falling through to unstable DEPTH comparison
        if (verticallyStacked && !xzOverlap) {
          // Ranges identical but no overlap means they're Y-separated
          if (ySepA > -EPSILON) {
            // A is above or at same level as B
            if (viewingFromBelow) {
              log('Y(stacked)', 0, -1);
              return -1;
            } else {
              log('Y(stacked)', 0, 1);
              return 1;
            }
          } else {
            if (viewingFromBelow) {
              log('Y(stacked)', 0, 1);
              return 1;
            } else {
              log('Y(stacked)', 0, -1);
              return -1;
            }
          }
        }

        // Handle edge-touching cases - treat as axis separation
        // When boxes touch at a boundary, the one on the "far" side is behind
        // IMPORTANT: Always return a result - never fall through to DEPTH
        if (zTouchingA && !zOverlap) {
          // A.minZ = B.maxZ: A is entirely at larger Z than B
          if (cosAngle > EPSILON) {
            log('Z(edge-touch)', 0, -1);
            return -1; // A is behind (larger Z)
          } else if (cosAngle < -EPSILON) {
            log('Z(edge-touch)', 0, 1);
            return 1; // B is behind
          } else {
            // cosAngle ≈ 0: default to larger Z behind
            log('Z(edge-touch,cos≈0)', 0, -1);
            return -1;
          }
        }
        if (zTouchingB && !zOverlap) {
          // B.minZ = A.maxZ: B is entirely at larger Z than A
          if (cosAngle > EPSILON) {
            log('Z(edge-touch)', 0, 1);
            return 1; // B is behind (larger Z)
          } else if (cosAngle < -EPSILON) {
            log('Z(edge-touch)', 0, -1);
            return -1; // A is behind
          } else {
            // cosAngle ≈ 0: default to larger Z behind
            log('Z(edge-touch,cos≈0)', 0, 1);
            return 1;
          }
        }
        if (xTouchingA && !xOverlap) {
          // A.minX = B.maxX: A is entirely at larger X than B
          if (sinAngle > EPSILON) {
            log('X(edge-touch)', 0, 1);
            return 1; // B is behind (smaller X)
          } else if (sinAngle < -EPSILON) {
            log('X(edge-touch)', 0, -1);
            return -1; // A is behind (larger X)
          } else {
            // sinAngle ≈ 0: default to smaller X behind
            log('X(edge-touch,sin≈0)', 0, 1);
            return 1;
          }
        }
        if (xTouchingB && !xOverlap) {
          // B.minX = A.maxX: B is entirely at larger X than A
          if (sinAngle > EPSILON) {
            log('X(edge-touch)', 0, -1);
            return -1; // A is behind (smaller X)
          } else if (sinAngle < -EPSILON) {
            log('X(edge-touch)', 0, 1);
            return 1; // B is behind (larger X)
          } else {
            // sinAngle ≈ 0: default to smaller X behind
            log('X(edge-touch,sin≈0)', 0, -1);
            return -1;
          }
        }

        // Use axis with largest separation (for non-touching cases)
        // IMPORTANT: When there's axis separation, ALWAYS use it - never fall through to DEPTH
        if (xSep > EPSILON || zSep > EPSILON) {
          if (xSep >= zSep) {
            // X-axis separation is dominant
            if (sinAngle > EPSILON) {
              // Smaller X is behind when sin > 0
              const result = xSepB > EPSILON ? -1 : 1;
              log('X', xSep, result);
              return result;
            } else if (sinAngle < -EPSILON) {
              // Larger X is behind when sin < 0
              const result = xSepA > EPSILON ? -1 : 1;
              log('X', xSep, result);
              return result;
            } else {
              // sinAngle ≈ 0: viewing along Z, use default convention (smaller X behind)
              const result = xSepB > EPSILON ? -1 : 1;
              log('X(sin≈0)', xSep, result);
              return result;
            }
          } else {
            // Z-axis separation is dominant
            if (cosAngle > EPSILON) {
              // Larger Z is behind when cos > 0
              const result = zSepA > EPSILON ? -1 : 1;
              log('Z', zSep, result);
              return result;
            } else if (cosAngle < -EPSILON) {
              // Smaller Z is behind when cos < 0
              const result = zSepB > EPSILON ? -1 : 1;
              log('Z', zSep, result);
              return result;
            } else {
              // cosAngle ≈ 0: viewing along X, use default convention (larger Z behind)
              const result = zSepA > EPSILON ? -1 : 1;
              log('Z(cos≈0)', zSep, result);
              return result;
            }
          }
        }

        // If Y separates but no XZ overlap (edge case)
        if (ySep > EPSILON) {
          if (viewingFromBelow) {
            const result = ySepA > EPSILON ? -1 : 1;
            log('Y(no-XZ-overlap)', ySep, result);
            return result;
          } else {
            const result = ySepB > EPSILON ? -1 : 1;
            log('Y(no-XZ-overlap)', ySep, result);
            return result;
          }
        }

        // No clear axis separation - use depth as final tiebreaker
        const depthA = computeDepth(a);
        const depthB = computeDepth(b);
        if (depthA > depthB + EPSILON) {
          if (logReason) Debug.log('DepthCycles', `    ${formatPos(a)} vs ${formatPos(b)}: DEPTH depthA=${depthA.toFixed(3)} > depthB=${depthB.toFixed(3)} → A behind`);
          return -1;
        }
        if (depthB > depthA + EPSILON) {
          if (logReason) Debug.log('DepthCycles', `    ${formatPos(a)} vs ${formatPos(b)}: DEPTH depthB=${depthB.toFixed(3)} > depthA=${depthA.toFixed(3)} → B behind`);
          return 1;
        }
        if (logReason) Debug.log('DepthCycles', `    ${formatPos(a)} vs ${formatPos(b)}: INDETERMINATE (depths equal)`);
        return 0; // Truly indeterminate
      }

      // Build constraint graph with definitive orderings
      const mustComeBefore = new Map();
      scc.forEach(node => mustComeBefore.set(node, new Set()));

      // Track constraint "strength" for cycle breaking
      const constraintStrength = new Map();

      if (DEBUG_CYCLES) Debug.log('DepthCycles', '  Pairwise axis separation analysis:');

      for (let i = 0; i < scc.length; i++) {
        for (let j = i + 1; j < scc.length; j++) {
          const a = scc[i];
          const b = scc[j];
          const order = getDefinitiveOrder(a, b, DEBUG_CYCLES);

          // PURE LOGICAL ANALYSIS for constraint classification
          // The key insight: SINGLE-axis separation is DEFINITIVE, DUAL-axis is AMBIGUOUS
          //
          // When only X separates (Z overlaps): X DEFINITIVELY determines order
          // When only Z separates (X overlaps): Z DEFINITIVELY determines order
          // When BOTH X and Z separate: Neither alone is definitive - this is AMBIGUOUS
          //
          // PRIORITY ORDER (highest to lowest):
          // 1. Y(XZ-overlap): True occlusion - UNBREAKABLE
          // 2. SINGLE horizontal axis separation (other overlaps): DEFINITIVE - very hard to break
          // 3. Y separation without XZ-overlap: Vertical ordering - moderate
          // 4. DUAL horizontal axis separation: AMBIGUOUS - break these FIRST
          // 5. Depth-based: Fallback - break easily
          function calcStrength(a, b) {
            // Check true overlaps (not touching)
            const xOverlap = a.minX < b.maxX - EPSILON && b.minX < a.maxX - EPSILON;
            const yOverlap = a.minY < b.maxY - EPSILON && b.minY < a.maxY - EPSILON;
            const zOverlap = a.minZ < b.maxZ - EPSILON && b.minZ < a.maxZ - EPSILON;

            // Calculate separations
            const ySep = Math.max(a.minY - b.maxY, b.minY - a.maxY, 0);
            const xSep = Math.max(a.minX - b.maxX, b.minX - a.maxX, 0);
            const zSep = Math.max(a.minZ - b.maxZ, b.minZ - a.maxZ, 0);

            // Check for edge-touching (counts as separated, not overlapping)
            const xTouch = Math.abs(a.minX - b.maxX) < EPSILON || Math.abs(b.minX - a.maxX) < EPSILON;
            const zTouch = Math.abs(a.minZ - b.maxZ) < EPSILON || Math.abs(b.minZ - a.maxZ) < EPSILON;
            const yTouch = Math.abs(a.minY - b.maxY) < EPSILON || Math.abs(b.minY - a.maxY) < EPSILON;

            // Distinguish between ACTUAL separation and EDGE-TOUCHING
            const xActuallySeparated = xSep > EPSILON;
            const zActuallySeparated = zSep > EPSILON;
            const xEdgeTouching = xTouch && !xActuallySeparated;
            const zEdgeTouching = zTouch && !zActuallySeparated;
            const xSeparated = xActuallySeparated || xEdgeTouching;
            const zSeparated = zActuallySeparated || zEdgeTouching;
            const ySeparated = ySep > EPSILON || yTouch;

            // === CONTAINMENT DETECTION ===
            // Y-containment: one object's Y range is fully within the other's
            const aYContainedInB = a.minY >= b.minY - EPSILON && a.maxY <= b.maxY + EPSILON;
            const bYContainedInA = b.minY >= a.minY - EPSILON && b.maxY <= a.maxY + EPSILON;
            const yContainment = aYContainedInB || bYContainedInA;

            // Shared base or top (objects at same vertical level)
            const sharedBase = Math.abs(a.minY - b.minY) < EPSILON;
            const sharedTop = Math.abs(a.maxY - b.maxY) < EPSILON;

            // X/Z containment: one object fully within other's horizontal extent
            // (Reserved for future use in more complex containment analysis)

            // LEVEL 1 (UNBREAKABLE): Y separation with full XZ overlap
            // TRUE VERTICAL OCCLUSION - one box is directly above another
            // Y-stacking with XZ overlap is ALWAYS important because vertical
            // ordering is deterministic regardless of view angle
            if (xOverlap && zOverlap && ySeparated) {
              // Touching or small gap - TRUE occlusion, never break
              return Infinity;
            }

            // LEVEL 2 (DEFINITIVE): SINGLE horizontal axis with ACTUAL separation
            // Real gap between boxes = geometrically certain ordering
            // DON'T weaken for Y-containment - the gap is still visible at that Y level
            if (xActuallySeparated && zOverlap && yOverlap) {
              return 10000000; // DEFINITIVE - actual visible gap
            }
            if (zActuallySeparated && xOverlap && yOverlap) {
              return 10000000; // DEFINITIVE - actual visible gap
            }

            // LEVEL 4 (EDGE-ADJACENT): SINGLE horizontal axis EDGE-TOUCHING with Y overlap
            // Boxes share a face - edge-touching is slightly weaker than actual separation
            // because the visual artifact from breaking is limited to the touching boundary
            if (xEdgeTouching && zOverlap && yOverlap) {
              // SHARED BASE: Strong but slightly weaker than actual separation
              // Edge-touching affects a smaller screen area than full overlap
              if (sharedBase) return 8000000; // Strong but breakable before actual separation
              if (sharedTop) return 7000000;
              // Y-containment WITHOUT shared base/top: one is floating within the other
              if (yContainment) return 5; // WEAK - can be broken
              return 100;
            }
            if (zEdgeTouching && xOverlap && yOverlap) {
              if (sharedBase) return 8000000;
              if (sharedTop) return 7000000;
              if (yContainment) return 5;
              return 100;
            }

            // LEVEL 5 (STRONG): Single horizontal separator without Y overlap
            if (xActuallySeparated && zOverlap && !yOverlap) {
              return 1000000;
            }
            if (zActuallySeparated && xOverlap && !yOverlap) {
              return 1000000;
            }

            // LEVEL 5b: Edge-touching without Y overlap
            if (xEdgeTouching && zOverlap && !yOverlap) {
              return 50;
            }
            if (zEdgeTouching && xOverlap && !yOverlap) {
              return 50;
            }

            // LEVEL 6 (MODERATE): Y separation without full XZ overlap
            if (ySeparated && (!xOverlap || !zOverlap)) {
              return 100000;
            }

            // LEVEL 7 (AMBIGUOUS): BOTH X and Z are separated
            // Boxes are "diagonal" - neither axis alone determines order
            if (xSeparated && zSeparated) {
              return 1;
            }

            // LEVEL 8 (FALLBACK): Depth-based only
            const depthDiff = Math.abs(computeDepth(a) - computeDepth(b));
            return depthDiff * 0.001;
          }

          if (order === -1) {
            mustComeBefore.get(a).add(b);
            constraintStrength.set(`${a.id}->${b.id}`, calcStrength(a, b));
          } else if (order === 1) {
            mustComeBefore.get(b).add(a);
            constraintStrength.set(`${b.id}->${a.id}`, calcStrength(a, b));
          } else {
            // order === 0 (indeterminate) - check original dependencies to preserve cycles
            // This is critical: Tarjan found these nodes in an SCC, so there must be edges
            // If getDefinitiveOrder can't determine order, use the original dependency direction
            const aHasDepOnB = dependencies.get(a)?.has(b);
            const bHasDepOnA = dependencies.get(b)?.has(a);

            if (aHasDepOnB && !bHasDepOnA) {
              // Original dependency: a depends on b (b must come before a)
              mustComeBefore.get(b).add(a);
              constraintStrength.set(`${b.id}->${a.id}`, 0.0001); // Very weak - prefer breaking this
              if (DEBUG_CYCLES) Debug.log('DepthCycles', `    ${formatPos(a)} vs ${formatPos(b)}: PRESERVED from original dep (b→a)`);
            } else if (bHasDepOnA && !aHasDepOnB) {
              // Original dependency: b depends on a (a must come before b)
              mustComeBefore.get(a).add(b);
              constraintStrength.set(`${a.id}->${b.id}`, 0.0001);
              if (DEBUG_CYCLES) Debug.log('DepthCycles', `    ${formatPos(a)} vs ${formatPos(b)}: PRESERVED from original dep (a→b)`);
            } else if (aHasDepOnB && bHasDepOnA) {
              // Both directions in original - this IS the cycle, pick one direction
              // Use depth to choose: whoever is further back goes first
              const depthA = computeDepth(a);
              const depthB = computeDepth(b);
              if (depthA > depthB) {
                mustComeBefore.get(a).add(b);
                constraintStrength.set(`${a.id}->${b.id}`, 0.0001);
              } else {
                mustComeBefore.get(b).add(a);
                constraintStrength.set(`${b.id}->${a.id}`, 0.0001);
              }
              if (DEBUG_CYCLES) Debug.log('DepthCycles', `    ${formatPos(a)} vs ${formatPos(b)}: BIDIRECTIONAL cycle edge, using depth`);
            }
            // If neither direction had an edge, they weren't directly connected - skip
          }
        }
      }

      // Kahn's algorithm with cycle detection and breaking
      function topologicalSortWithCycleBreaking() {
        const inDegree = new Map();
        const currentDeps = new Map();
        scc.forEach(node => {
          inDegree.set(node, 0);
          currentDeps.set(node, new Set(mustComeBefore.get(node)));
        });

        for (const [, afters] of currentDeps) {
          for (const after of afters) {
            inDegree.set(after, inDegree.get(after) + 1);
          }
        }

        const result = [];
        const resultSet = new Set(); // O(1) membership check
        let available = scc.filter(node => inDegree.get(node) === 0);
        available.sort((a, b) => computeDepth(b) - computeDepth(a));

        // Max cycle breaks to prevent infinite loops in pathological cases
        let cycleBreaks = 0;
        const MAX_CYCLE_BREAKS = scc.length * 2;

        while (result.length < scc.length) {
          if (available.length > 0) {
            // Normal case: take available node with greatest depth
            const node = available.shift();
            result.push(node);
            resultSet.add(node);

            for (const after of currentDeps.get(node)) {
              const newDegree = inDegree.get(after) - 1;
              inDegree.set(after, newDegree);
              if (newDegree === 0) {
                // Binary insertion to keep available sorted by depth (descending)
                const d = computeDepth(after);
                let lo = 0, hi = available.length;
                while (lo < hi) {
                  const mid = (lo + hi) >> 1;
                  if (computeDepth(available[mid]) > d) lo = mid + 1;
                  else hi = mid;
                }
                available.splice(lo, 0, after);
              }
            }
          } else {
            // Time/iteration budget check inside Kahn's
            if (++cycleBreaks > MAX_CYCLE_BREAKS ||
                (startTime && performance.now() - startTime > TIME_BUDGET_MS)) {
              // Budget exceeded — depth-sort remaining nodes and bail
              const remaining = scc.filter(n => !resultSet.has(n));
              remaining.sort((a, b) => computeDepth(b) - computeDepth(a));
              result.push(...remaining);
              break;
            }

            // Cycle detected! Find and break the weakest constraint
            // IMPORTANT: Only consider edges that are actually part of cycles
            const remainingSet = new Set(scc.filter(n => !resultSet.has(n)));

            // Find SCCs in the remaining subgraph to identify actual cycles
            function findRemainingCycles() {
              const index = new Map();
              const lowlink = new Map();
              const onStack = new Map();
              const stack = [];
              let idx = 0;
              const sccs = [];

              function strongconnect(node) {
                index.set(node, idx);
                lowlink.set(node, idx);
                idx++;
                stack.push(node);
                onStack.set(node, true);

                for (const neighbor of currentDeps.get(node)) {
                  if (!remainingSet.has(neighbor)) continue; // O(1) Set check
                  if (!index.has(neighbor)) {
                    strongconnect(neighbor);
                    lowlink.set(node, Math.min(lowlink.get(node), lowlink.get(neighbor)));
                  } else if (onStack.get(neighbor)) {
                    lowlink.set(node, Math.min(lowlink.get(node), index.get(neighbor)));
                  }
                }

                if (lowlink.get(node) === index.get(node)) {
                  const component = [];
                  let w;
                  do {
                    w = stack.pop();
                    onStack.set(w, false);
                    component.push(w);
                  } while (w !== node);
                  if (component.length > 1) {
                    sccs.push(new Set(component));
                  }
                }
              }

              for (const node of remainingSet) {
                if (!index.has(node)) {
                  strongconnect(node);
                }
              }
              return sccs;
            }

            const cyclicSCCs = findRemainingCycles();

            // Collect ALL edges within cyclic SCCs, sorted by strength
            // This allows us to try multiple edges for subdivision, not just the weakest
            const cyclicEdges = [];

            for (const cyclicSet of cyclicSCCs) {
              for (const from of cyclicSet) {
                for (const to of currentDeps.get(from)) {
                  if (cyclicSet.has(to)) {
                    const key = `${from.id}->${to.id}`;
                    const strength = constraintStrength.get(key) || 0;

                    // Check if either cuboid can accept more fragments (use fast exhaustion check)
                    // Sprites can never be split
                    const parentKeyFrom = from.parentKey || from.cuboid.getKey();
                    const parentKeyTo = to.parentKey || to.cuboid.getKey();
                    const canSplitFrom = !from.isSprite && !world.kdTree.isExhausted(parentKeyFrom) && world.kdTree.canAddFragments(parentKeyFrom, true);
                    const canSplitTo = !to.isSprite && !world.kdTree.isExhausted(parentKeyTo) && world.kdTree.canAddFragments(parentKeyTo, true);
                    const canSplitEither = canSplitFrom || canSplitTo;

                    cyclicEdges.push({ from, to, strength, key, canSplitEither });
                  }
                }
              }
            }

            // Sort by: 1) edges where we CAN split first, 2) then by strength (weakest first)
            cyclicEdges.sort((a, b) => {
              if (a.canSplitEither && !b.canSplitEither) return -1;
              if (!a.canSplitEither && b.canSplitEither) return 1;
              return a.strength - b.strength;
            });

            // Fallback: if no cyclic SCCs found but we're stuck, collect global edges
            if (cyclicEdges.length === 0) {
              for (const from of remainingSet) {
                for (const to of currentDeps.get(from)) {
                  if (remainingSet.has(to)) {
                    const key = `${from.id}->${to.id}`;
                    const strength = constraintStrength.get(key) || 0;

                    const parentKeyFrom = from.parentKey || from.cuboid.getKey();
                    const parentKeyTo = to.parentKey || to.cuboid.getKey();
                    const canSplitFrom = !from.isSprite && !world.kdTree.isExhausted(parentKeyFrom) && world.kdTree.canAddFragments(parentKeyFrom, true);
                    const canSplitTo = !to.isSprite && !world.kdTree.isExhausted(parentKeyTo) && world.kdTree.canAddFragments(parentKeyTo, true);
                    const canSplitEither = canSplitFrom || canSplitTo;

                    cyclicEdges.push({ from, to, strength, key, canSplitEither });
                  }
                }
              }
              cyclicEdges.sort((a, b) => {
                if (a.canSplitEither && !b.canSplitEither) return -1;
                if (!a.canSplitEither && b.canSplitEither) return 1;
                return a.strength - b.strength;
              });
            }

            // Collect split candidates into global plan (deferred execution after all SCCs)
            const MAX_EDGES_TO_EXAMINE = 20;
            let edgeToRemove = cyclicEdges[0]; // Default to weakest

            if (cyclicEdges.length > 0) {
              collectEdgesIntoGlobalPlan(cyclicEdges, MAX_EDGES_TO_EXAMINE);
            }

            if (cyclicEdges.length > 0) {
              const weakestEdge = edgeToRemove;
              const minStrength = weakestEdge.strength;

              // Remove the edge (either the one we split on, or the weakest if no split worked)
              currentDeps.get(weakestEdge.from).delete(weakestEdge.to);
              const newDegree = inDegree.get(weakestEdge.to) - 1;
              inDegree.set(weakestEdge.to, newDegree);
              if (newDegree === 0) {
                // Binary insertion to keep available sorted by depth (descending)
                const d = computeDepth(weakestEdge.to);
                let lo = 0, hi = available.length;
                while (lo < hi) {
                  const mid = (lo + hi) >> 1;
                  if (computeDepth(available[mid]) > d) lo = mid + 1;
                  else hi = mid;
                }
                available.splice(lo, 0, weakestEdge.to);
              }
              if (DEBUG_CYCLES) {
                // Categorize the broken constraint for debugging
                let category;
                if (minStrength === Infinity) category = 'UNBREAKABLE(!)';
                else if (minStrength >= 10000000) category = 'DEFINITIVE';
                else if (minStrength >= 1000000) category = 'STRONG';
                else if (minStrength >= 100000) category = 'MODERATE';
                else if (minStrength >= 1000) category = 'Y-STACKING(gap)';
                else if (minStrength >= 100) category = 'EDGE-ADJACENT';
                else if (minStrength >= 50) category = 'EDGE-ADJACENT(no Y)';
                else if (minStrength >= 5) category = 'EDGE+Y-CONTAINED';
                else if (minStrength >= 1) category = 'AMBIGUOUS(X+Z sep)';
                else category = 'FALLBACK';
                Debug.log('DepthCycles', `  [Cycle Break] Removed ${category} edge ${formatPos(weakestEdge.from)} → ${formatPos(weakestEdge.to)} (strength=${minStrength.toFixed(4)})`);
              }
            } else {
              // Shouldn't happen, but fallback to depth sort
              const remainArr = [...remainingSet];
              remainArr.sort((a, b) => computeDepth(b) - computeDepth(a));
              result.push(...remainArr);
              break;
            }
          }
        }

        return result;
      }

      const result = topologicalSortWithCycleBreaking();

      // CRITICAL: If Tarjan found an SCC with >1 members, there IS a cycle in the original
      // dependency graph. If topologicalSortWithCycleBreaking didn't collect split candidates,
      // it means getDefinitiveOrder produced a DAG (no cycle in mustComeBefore).
      // This happens when DEPTH-based comparisons give consistent but WRONG ordering.
      // We must force subdivision to break the geometric cycle.
      // Check globalSplitPlan instead of subdivisionOccurred since splits are deferred.
      if (scc.length > 1 && globalSplitPlan.size === 0 && !timeBudgetExceeded) {
        // First check if ANY cuboid in the SCC can still be split
        let anyCuboidCanSplit = false;
        for (const p of scc) {
          if (p.isSprite) continue; // Sprites can't be split
          const parentKey = p.parentKey || p.cuboid.getKey();
          if (!world.kdTree.isExhausted(parentKey)) {
            anyCuboidCanSplit = true;
            break;
          }
        }

        if (!anyCuboidCanSplit) {
          // All cuboids exhausted - skip force subdivision entirely
          if (DEBUG_CYCLES) {
            Debug.log('DepthCycles', '  [Force Subdivision] SKIPPED - all cuboids exhausted, using depth sort');
          }
        } else {
          if (DEBUG_CYCLES) {
            Debug.log('DepthCycles', '  [Force Subdivision] SCC has cycle but mustComeBefore is DAG - forcing subdivision');
          }

          // Collect edges from the ORIGINAL dependencies (which has the cycle)
          const originalCyclicEdges = [];
          for (const from of scc) {
            for (const to of scc) {
              if (from !== to && dependencies.get(from)?.has(to)) {
                // This edge exists in the original dependency graph
                const key = `${from.id}->${to.id}`;
                const strength = constraintStrength.get(key) || 0;

                // Check if either cuboid can accept more fragments (use fast exhaustion check)
                // Sprites can never be split
                const parentKeyFrom = from.parentKey || from.cuboid.getKey();
                const parentKeyTo = to.parentKey || to.cuboid.getKey();
                const canSplitFrom = !from.isSprite && !world.kdTree.isExhausted(parentKeyFrom) && world.kdTree.canAddFragments(parentKeyFrom, true);
                const canSplitTo = !to.isSprite && !world.kdTree.isExhausted(parentKeyTo) && world.kdTree.canAddFragments(parentKeyTo, true);
                const canSplitEither = canSplitFrom || canSplitTo;

                originalCyclicEdges.push({ from, to, strength, key, canSplitEither, canSplitFrom, canSplitTo });
              }
            }
          }

          // Sort by: 1) edges where we CAN split first, 2) then by strength (weakest first)
          originalCyclicEdges.sort((a, b) => {
            // Prioritize edges where at least one cuboid can be split
            if (a.canSplitEither && !b.canSplitEither) return -1;
            if (!a.canSplitEither && b.canSplitEither) return 1;
            // Then by strength
            return a.strength - b.strength;
          });

          collectEdgesIntoGlobalPlan(originalCyclicEdges);

          if (DEBUG_CYCLES) {
            Debug.log('DepthCycles', `  [Force Subdivision] Collected ${globalSplitPlan.size} cuboids into global plan`);
          }
        } // end else (anyCuboidCanSplit)
      }

      // Log final ordering
      if (DEBUG_CYCLES) {
        Debug.log('DepthCycles', '  Final order:');
        result.forEach((p, i) => Debug.log('DepthCycles', `    ${i}: ${formatPos(p)}`));
        console.groupEnd();
      }

      // Update scc in place
      for (let i = 0; i < scc.length; i++) {
        scc[i] = result[i];
      }
    }

    // Process ALL SCCs in a single pass (collecting all subdivisions)
    // then recurse once with all new fragments, instead of breaking after first SCC
    // Check time budget between SCCs to prevent blocking the main thread
    let timeBudgetExceeded = startTime && (performance.now() - startTime > TIME_BUDGET_MS);
    const perfT4 = PERF ? performance.now() : 0;
    for (const scc of sccs) {
      if (startTime && performance.now() - startTime > TIME_BUDGET_MS) {
        timeBudgetExceeded = true;
      }
      resolveCycleWithFullSAT(scc, timeBudgetExceeded);
    }
    if (PERF) {
      const perfT5 = performance.now();
      Debug.log('SortPerf', `  SCC resolution: ${(perfT5 - perfT4).toFixed(1)}ms, globalSplitPlan: ${globalSplitPlan.size}, timeBudget: ${timeBudgetExceeded}`);
    }

    // Execute global split plan: invalidate-and-re-split from original cuboid.
    // Instead of sub-splitting individual fragments (which causes unbounded accumulation),
    // merge new split positions with accumulated ones and re-split the original cuboid
    // from scratch. This bounds fragment count to the optimal grid size.
    if (globalSplitPlan.size > 0 && !timeBudgetExceeded) {
      const DEBUG_CYCLES = Debug.enabled && Debug.isCategoryEnabled('DepthCycles');
      for (const [cuboidKey, { cuboid, posRef, axisSplits }] of globalSplitPlan) {
        if (!cuboid) continue;
        const parentKey = posRef.parentKey || cuboid.getKey();

        // Merge new split positions with accumulated positions from previous frames.
        // EPSILON dedup prevents floating-point drift from inflating the set.
        let anyNewPositions = false;
        for (const [axis, positions] of axisSplits) {
          const { changed } = this.kdTree.mergeAccumulatedSplits(parentKey, axis, positions);
          if (changed) anyNewPositions = true;
        }

        // If no new positions were discovered and fragments already exist at the
        // current accumulated positions, skip — avoids destroying and recreating
        // identical DOM elements every frame.
        if (!anyNewPositions && this.kdTree.hasFragments(parentKey)) continue;

        const accumulated = this.kdTree.accumulatedSplits.get(parentKey);

        // Get the ORIGINAL cuboid — always re-split from original, never sub-split fragments
        const originalCuboid = this.cuboids.get(parentKey);
        if (!originalCuboid) continue;

        // Check fragment limits against the PLANNED grid size
        const gridSize = (accumulated.x.size + 1) * (accumulated.z.size + 1) * (accumulated.y.size + 1);
        if (gridSize > this.kdTree.MAX_FRAGMENTS_PER_CUBOID) continue;

        // Invalidate existing fragments (removes DOM elements, cleans up videos)
        if (this.kdTree.hasFragments(parentKey)) {
          this.kdTree.invalidateCuboid(parentKey);
        }

        // Build fresh fragments from original cuboid with ALL accumulated positions
        const xPositions = [...accumulated.x];
        const zPositions = [...accumulated.z];
        const yPositions = [...accumulated.y];

        const axisCount = (xPositions.length > 0 ? 1 : 0) +
                          (zPositions.length > 0 ? 1 : 0) +
                          (yPositions.length > 0 ? 1 : 0);

        let fragments;
        if (axisCount >= 2) {
          fragments = originalCuboid.gridSubdivide(xPositions, zPositions, yPositions);
          if (DEBUG_CYCLES) {
            Debug.log('DepthCycles', `  [GlobalPlan] gridSubdivide ${parentKey}: X[${xPositions.join(',')}] Z[${zPositions.join(',')}] Y[${yPositions.join(',')}] → ${fragments ? fragments.length : 0} fragments`);
          }
        } else {
          let bestAxis, bestPositions;
          if (xPositions.length > 0) { bestAxis = 'x'; bestPositions = xPositions; }
          else if (zPositions.length > 0) { bestAxis = 'z'; bestPositions = zPositions; }
          else { bestAxis = 'y'; bestPositions = yPositions; }
          if (bestAxis && bestPositions.length > 0) {
            fragments = originalCuboid.multiSubdivide(bestAxis, bestPositions);
            if (DEBUG_CYCLES) {
              Debug.log('DepthCycles', `  [GlobalPlan] multiSubdivide ${parentKey} on ${bestAxis} at [${bestPositions.join(',')}] → ${fragments ? fragments.length : 0} fragments`);
            }
          }
        }

        if (!fragments || fragments.length === 0) continue;

        this.kdTree.addFragments(parentKey, fragments, this.element);
        this.kdTree.hideOriginal(originalCuboid);
        subdivisionOccurred = true;
      }
    }

    // If subdivision occurred, we need to re-run the sort with the new fragments
    if (subdivisionOccurred && !timeBudgetExceeded) {
      if (subdivisionDepth < MAX_SUBDIVISION_PASSES) {
        // Circuit breaker: check if further subdivision is even possible
        // If all cuboids are now exhausted, recursing won't help
        let anyCanStillSplit = false;
        for (const [key] of this.cuboids) {
          if (!this.kdTree.isExhausted(key)) {
            anyCanStillSplit = true;
            break;
          }
        }
        if (!anyCanStillSplit) {
          // All cuboids exhausted - no point recursing, fall through to render
        } else {
          return this.sortAndRenderCuboids(subdivisionDepth + 1, startTime);
        }
      }
    }

    // Build a map from node to its SCC
    const nodeToSCC = new Map();
    sccs.forEach((scc, idx) => {
      scc.forEach(node => nodeToSCC.set(node, idx));
    });

    // Build DAG of SCCs
    const sccDeps = new Map();
    sccs.forEach((_, idx) => sccDeps.set(idx, new Set()));

    for (const [node, deps] of dependencies) {
      const nodeSCC = nodeToSCC.get(node);
      for (const dep of deps) {
        const depSCC = nodeToSCC.get(dep);
        if (nodeSCC !== depSCC) {
          sccDeps.get(nodeSCC).add(depSCC);
        }
      }
    }

    // Topological sort on SCCs (now guaranteed to be a DAG)
    function topologicalSortSCCs(sccCount, deps) {
      const result = [];
      const visited = new Set();

      function visit(sccIdx) {
        if (visited.has(sccIdx)) return;
        visited.add(sccIdx);

        const sccDepsSet = deps.get(sccIdx) || new Set();
        for (const depIdx of sccDepsSet) {
          visit(depIdx);
        }
        result.push(sccIdx);
      }

      for (let i = 0; i < sccCount; i++) {
        visit(i);
      }
      return result;
    }

    const sortedSCCIndices = topologicalSortSCCs(sccs.length, sccDeps);

    // Flatten: output nodes in SCC order, with each SCC's internal depth-sorted order
    const sortedPositions = [];
    for (const sccIdx of sortedSCCIndices) {
      for (const node of sccs[sccIdx]) {
        sortedPositions.push(node);
      }
    }

    // Depth-sort consecutive sprite runs for correct sprite-vs-sprite ordering.
    // SAT handles sprite-cuboid ordering, but sprite pairs on the same surface
    // can end up in arbitrary order after cycle resolution. Sorting within bounded
    // runs fixes this without disturbing sprite-cuboid relationships.
    let spriteRunStart = -1;
    for (let i = 0; i <= sortedPositions.length; i++) {
      const isSprite = i < sortedPositions.length && sortedPositions[i].isSprite;
      if (isSprite && spriteRunStart === -1) {
        spriteRunStart = i;
      } else if (!isSprite && spriteRunStart !== -1) {
        if (i - spriteRunStart > 1) {
          const run = sortedPositions.slice(spriteRunStart, i);
          run.sort((a, b) => computeDepth(b) - computeDepth(a));
          for (let j = 0; j < run.length; j++) {
            sortedPositions[spriteRunStart + j] = run[j];
          }
        }
        spriteRunStart = -1;
      }
    }

    // Render in sorted order using DocumentFragment for performance

    // Track rendered submerged attachments to avoid duplicates (when attached to multiple cuboids)
    // Reuse Set to avoid per-frame allocation
    const renderedAttachments = this._frameRenderedAttachments;
    renderedAttachments.clear();

    // First pass: find webpage cuboid (limited to one per scene for DOM stability)
    let webpageCuboidKey = null;
    let webpageCuboidElement = null;
    for (const pos of sortedPositions) {
      if (!pos.isSprite && !pos.isGroundEffect && pos.cuboid?.hasWebpageMaterial?.()) {
        // Skip split originals
        const cuboidKey = pos.cuboid.getKey();
        if (!pos.isFragment && this.kdTree.splitCuboids.has(cuboidKey)) {
          continue;
        }
        webpageCuboidKey = cuboidKey;
        webpageCuboidElement = pos.cuboid.element;
        break; // Only one webpage cuboid supported
      }
    }

    // Check if webpage cuboid is already in the DOM (not first render).
    // If the element exists but was somehow detached (e.g. by a previous innerHTML=''
    // when it was off-screen), re-attach it now so the DOM-stable path can preserve it.
    // Without this, the standard path's innerHTML='' would destroy and reload the iframe.
    let webpageInDOM = webpageCuboidElement && webpageCuboidElement.parentNode === this.element;
    if (webpageCuboidElement && !webpageInDOM) {
      // Re-attach detached webpage cuboid to preserve iframe state
      this.element.appendChild(webpageCuboidElement);
      webpageInDOM = true;
    }

    // Debug: find webpage position in sorted order
    if (webpageCuboidKey) {
      let webpagePos = -1;
      let totalCuboids = 0;
      for (let i = 0; i < sortedPositions.length; i++) {
        const pos = sortedPositions[i];
        if (!pos.isSprite && !pos.isGroundEffect) {
          const key = pos.cuboid.getKey();
          if (!pos.isFragment && this.kdTree.splitCuboids.has(key)) continue;
          totalCuboids++;
          if (key === webpageCuboidKey) webpagePos = totalCuboids;
        }
      }
      if (this._lastWebpageDebug !== `${webpagePos}/${totalCuboids}`) {
        this._lastWebpageDebug = `${webpagePos}/${totalCuboids}`;
        Debug.log('Webpage', `Position in depth order: ${webpagePos} of ${totalCuboids} cuboids`);
      }
    }

    if (webpageInDOM) {
      // DOM-stable approach: reorder around the iframe to prevent reload
      // Remove all children except the webpage cuboid
      const childrenToRemove = [];
      for (const child of this.element.children) {
        if (child !== webpageCuboidElement) {
          childrenToRemove.push(child);
        }
      }
      childrenToRemove.forEach(child => child.remove());

      // Build before/after fragments
      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();
      let foundWebpage = false;

      for (const pos of sortedPositions) {
        let elementToAdd = null;

        if (pos.isSprite) {
          // Update sprite screen position (direction, atlas frame, left/top)
          if (pos.character?.sprite) {
            pos.character.sprite.update(this.camera);
          }
          pos.element.style.display = '';
          elementToAdd = pos.element;
        } else if (pos.isGroundEffect) {
          pos.element.style.display = '';
          pos.groundEffect.updateTransform(this.camera);
          elementToAdd = pos.element;
        } else {
          // Cuboid - handle split originals by rendering their fragments inline
          const cuboidKey = pos.cuboid.getKey();
          if (!pos.isFragment && this.kdTree.splitCuboids.has(cuboidKey)) {
            // Fragments were created during SCC resolution but aren't in sortedPositions.
            // Render them here at the original's position to prevent one-frame disappearance.
            const targetFrag = !foundWebpage ? beforeFragment : afterFragment;

            // Keep original in DOM (hidden) so its video elements keep playing.
            // Resume videos so captureStream() keeps producing frames for fragments.
            pos.cuboid.element.style.display = '';
            pos.cuboid.element.style.visibility = 'hidden';
            pos.cuboid.resumeVideos();
            targetFrag.appendChild(pos.cuboid.element);

            // Collect all renderable items: water fragments + submerged sprite attachments
            // Then depth-sort them together so sprites interleave correctly between
            // front and back water fragments (not all-before-all).
            const renderItems = [];

            if (this._submergedAttachments && this._submergedAttachments.has(cuboidKey)) {
              const attachments = this._submergedAttachments.get(cuboidKey);
              for (const att of attachments) {
                if (!renderedAttachments.has(att.element)) {
                  renderItems.push({ element: att.element, depth: att.depth, type: 'attachment' });
                }
              }
            }

            const frags = this.kdTree.getFragments(cuboidKey);
            if (frags) {
              for (const frag of frags) {
                const farX = sinAngle > 0 ? frag.x : frag.x + frag.width;
                const farZ = cosAngle > 0 ? frag.z + frag.depth : frag.z;
                const farY = viewingFromBelow ? frag.y + frag.height : frag.y;
                const depth = -farX * sinAngle + farZ * cosAngle + farY * 0.001 * (viewingFromBelow ? 1 : -1);
                renderItems.push({ element: frag.element, depth, type: 'fragment', frag });
              }
            }

            // Sort by depth descending (farthest first → behind in DOM order)
            renderItems.sort((a, b) => b.depth - a.depth);

            for (const item of renderItems) {
              if (item.type === 'attachment') {
                item.element.style.display = '';
                targetFrag.appendChild(item.element);
                renderedAttachments.add(item.element);
              } else {
                item.frag.element.style.display = '';
                item.frag.element.style.visibility = '';
                item.frag.update(this.camera);
                item.frag.resumeVideos();
                item.frag.element.style.zIndex = '';
                targetFrag.appendChild(item.frag.element);
              }
            }
            continue;
          }

          // Handle submerged sprite attachments (sorted by depth for correct ordering)
          // Check both the cuboid's own key and its parent key (for fragments of split cuboids,
          // submerged attachments are registered under the original parent key)
          const attachKeys = [cuboidKey];
          if (pos.parentKey && pos.parentKey !== cuboidKey) {
            attachKeys.push(pos.parentKey);
          }
          for (const attachKey of attachKeys) {
            if (this._submergedAttachments && this._submergedAttachments.has(attachKey)) {
              const attachments = this._submergedAttachments.get(attachKey);
              // Sort farthest first (descending depth) so closest renders last (on top)
              if (attachments.length > 1) attachments.sort((a, b) => b.depth - a.depth);
              for (const att of attachments) {
                if (!renderedAttachments.has(att.element)) {
                  if (!foundWebpage) {
                    beforeFragment.appendChild(att.element);
                  } else {
                    afterFragment.appendChild(att.element);
                  }
                  renderedAttachments.add(att.element);
                }
              }
            }
          }

          pos.cuboid.element.style.display = '';
          pos.cuboid.element.style.visibility = ''; // Reset visibility (may have been hidden for webpage culling)
          pos.cuboid.resumeVideos();
          pos.cuboid.update(this.camera);
          pos.cuboid.element.style.zIndex = '';

          // Check if this is the webpage cuboid (use both key AND element comparison for safety)
          const isWebpageCuboid = cuboidKey === webpageCuboidKey || pos.cuboid.element === webpageCuboidElement;
          if (isWebpageCuboid) {
            foundWebpage = true;
            continue; // Don't add webpage to any fragment - it stays in place
          }

          elementToAdd = pos.cuboid.element;
        }

        if (elementToAdd) {
          if (!foundWebpage) {
            beforeFragment.appendChild(elementToAdd);
          } else {
            afterFragment.appendChild(elementToAdd);
          }
        }
      }

      // Apply group materials to fragments BEFORE inserting into DOM
      // This ensures fragments have correct triplanar textures when they first become visible,
      // preventing single-frame single-face texture flash on subdivision
      this._applyFragmentMaterials(sortedPositions);

      // Insert fragments around the webpage element
      // Debug: log fragment sizes
      const beforeCount = beforeFragment.childNodes.length;
      const afterCount = afterFragment.childNodes.length;
      if (this._lastFragmentDebug !== `${beforeCount}/${afterCount}`) {
        this._lastFragmentDebug = `${beforeCount}/${afterCount}`;
        Debug.log('Webpage', `Fragments: ${beforeCount} before, ${afterCount} after`);
      }

      if (beforeCount > 0) {
        this.element.insertBefore(beforeFragment, webpageCuboidElement);
      }
      if (afterCount > 0) {
        this.element.appendChild(afterFragment);
      }

      // Debug: verify final DOM order
      const children = Array.from(this.element.children);
      const webpageIdx = children.indexOf(webpageCuboidElement);
      if (this._lastDOMDebug !== `${webpageIdx}/${children.length}`) {
        this._lastDOMDebug = `${webpageIdx}/${children.length}`;
        Debug.log('Webpage', `Final DOM position: ${webpageIdx + 1} of ${children.length}`);
      }
    } else {
      // LIS-based DOM reconciliation: compute desired element order, diff against
      // current DOM children, and apply the minimum number of moves. During smooth
      // camera rotation most elements keep the same relative order — only the few
      // that swap depth positions need DOM mutations. This preserves browser
      // compositor layers (no innerHTML='' destruction) while being as fast as
      // or faster than the old batched approach for steady-state rendering.

      // Phase 1: Compute desired order (update element styles, don't touch DOM order)
      // Reuse arrays to avoid per-frame allocation
      const desiredOrder = this._frameDesiredOrder;
      desiredOrder.length = 0;
      const desiredSet = this._frameDesiredSet;
      desiredSet.clear();

      for (const pos of sortedPositions) {
        if (pos.isSprite) {
          if (pos.character?.sprite) {
            pos.character.sprite.update(this.camera);
          }
          pos.element.style.display = '';
          desiredOrder.push(pos.element);
          desiredSet.add(pos.element);
        } else if (pos.isGroundEffect) {
          pos.element.style.display = '';
          pos.groundEffect.updateTransform(this.camera);
          desiredOrder.push(pos.element);
          desiredSet.add(pos.element);
        } else {
          const cuboidKey = pos.cuboid.getKey();
          if (!pos.isFragment && this.kdTree.splitCuboids.has(cuboidKey)) {
            pos.cuboid.element.style.display = '';
            pos.cuboid.element.style.visibility = 'hidden';
            pos.cuboid.resumeVideos();
            desiredOrder.push(pos.cuboid.element);
            desiredSet.add(pos.cuboid.element);

            const renderItems = [];
            if (this._submergedAttachments && this._submergedAttachments.has(cuboidKey)) {
              const attachments = this._submergedAttachments.get(cuboidKey);
              for (const att of attachments) {
                if (!renderedAttachments.has(att.element)) {
                  renderItems.push({ element: att.element, depth: att.depth, type: 'attachment' });
                }
              }
            }
            const frags = this.kdTree.getFragments(cuboidKey);
            if (frags) {
              for (const frag of frags) {
                const farX = sinAngle > 0 ? frag.x : frag.x + frag.width;
                const farZ = cosAngle > 0 ? frag.z + frag.depth : frag.z;
                const farY = viewingFromBelow ? frag.y + frag.height : frag.y;
                const depth = -farX * sinAngle + farZ * cosAngle + farY * 0.001 * (viewingFromBelow ? 1 : -1);
                renderItems.push({ element: frag.element, depth, type: 'fragment', frag });
              }
            }
            renderItems.sort((a, b) => b.depth - a.depth);
            for (const item of renderItems) {
              if (item.type === 'attachment') {
                item.element.style.display = '';
                desiredOrder.push(item.element);
                desiredSet.add(item.element);
                renderedAttachments.add(item.element);
              } else {
                item.frag.element.style.display = '';
                item.frag.element.style.visibility = '';
                item.frag.update(this.camera);
                item.frag.resumeVideos();
                item.frag.element.style.zIndex = '';
                desiredOrder.push(item.frag.element);
                desiredSet.add(item.frag.element);
              }
            }
            continue;
          }

          const attachKeys = [cuboidKey];
          if (pos.parentKey && pos.parentKey !== cuboidKey) {
            attachKeys.push(pos.parentKey);
          }
          for (const attachKey of attachKeys) {
            if (this._submergedAttachments && this._submergedAttachments.has(attachKey)) {
              const attachments = this._submergedAttachments.get(attachKey);
              if (attachments.length > 1) attachments.sort((a, b) => b.depth - a.depth);
              for (const att of attachments) {
                if (!renderedAttachments.has(att.element)) {
                  desiredOrder.push(att.element);
                  desiredSet.add(att.element);
                  renderedAttachments.add(att.element);
                }
              }
            }
          }

          pos.cuboid.element.style.display = '';
          pos.cuboid.element.style.visibility = '';
          pos.cuboid.resumeVideos();
          pos.cuboid.update(this.camera);
          pos.cuboid.element.style.zIndex = '';
          desiredOrder.push(pos.cuboid.element);
          desiredSet.add(pos.cuboid.element);
        }
      }

      this._applyFragmentMaterials(sortedPositions);

      // Preserve off-screen iframe cuboids in DOM. Removing an iframe from the
      // DOM causes the browser to reload it when re-appended, freezing the game
      // for seconds. Keep culled iframe elements hidden in the DOM so the
      // DOM-stable path can position them correctly when they become visible.
      for (const [, cuboid] of this.cuboids) {
        if (cuboid.hasWebpageMaterial?.() && cuboid.element && !desiredSet.has(cuboid.element)) {
          cuboid.element.style.display = '';
          cuboid.element.style.visibility = 'hidden';
          desiredOrder.push(cuboid.element);
          desiredSet.add(cuboid.element);
        }
      }

      // Phase 2: Apply DOM changes using chosen strategy
      if (this.useVirtualDOM) {
        // LIS-based reconciliation: minimum DOM mutations
        this._reconcileChildren(desiredOrder, desiredSet);
      } else {
        // Legacy: clear and rebuild via fragment (fewer compositor layers to track).
        // If an iframe cuboid exists, preserve it — innerHTML='' destroys it and
        // re-appending causes a full page reload that freezes for seconds.
        let iframeEl = null;
        for (const [, cuboid] of this.cuboids) {
          if (cuboid.hasWebpageMaterial?.() && cuboid.element && cuboid.element.parentNode === this.element) {
            iframeEl = cuboid.element;
            break; // Only one iframe cuboid per scene
          }
        }

        if (iframeEl) {
          // Remove all children except the iframe
          for (let i = this.element.children.length - 1; i >= 0; i--) {
            const child = this.element.children[i];
            if (child !== iframeEl) child.remove();
          }
          // Rebuild around iframe using before/after fragments
          const beforeFrag = document.createDocumentFragment();
          const afterFrag = document.createDocumentFragment();
          let afterIframe = false;
          for (let i = 0; i < desiredOrder.length; i++) {
            if (desiredOrder[i] === iframeEl) {
              afterIframe = true;
              continue;
            }
            (afterIframe ? afterFrag : beforeFrag).appendChild(desiredOrder[i]);
          }
          if (beforeFrag.childNodes.length > 0) {
            this.element.insertBefore(beforeFrag, iframeEl);
          }
          if (afterFrag.childNodes.length > 0) {
            this.element.appendChild(afterFrag);
          }
        } else {
          this.element.innerHTML = '';
          const fragment = document.createDocumentFragment();
          for (let i = 0; i < desiredOrder.length; i++) {
            fragment.appendChild(desiredOrder[i]);
          }
          this.element.appendChild(fragment);
        }
      }
    }

    // Re-apply character shadows after fragment creation.
    // CharacterShadow.update() runs during updateCharacters() BEFORE sortAndRenderCuboids(),
    // so it targets stale fragment objects that get destroyed and rebuilt during sorting.
    // This re-application targets the CURRENT frame's fragment faces.
    if (this.kdTree.splitCuboids.size > 0) {
      for (const character of this.characters.values()) {
        if (character.shadow) {
          character.shadow.reapplyForFragments(this.camera);
        }
      }
    }

    // Check if zoom changed and refresh material group styles AFTER all cuboid updates.
    // On Android, cuboid.update() calls refreshMaterials() which clears face styles.
    // This ensures triplanar group materials are re-applied on top.
    this.materialGroupManager.checkZoomAndRefresh(this.camera.zoomLevel);

    // Restore visibility after render is complete (hidden at depth=0 start to prevent flash)
    this.element.style.visibility = '';

    // Clear dirty cuboids tracking after successful render
    this._dirtyCuboids.clear();

    // Resample impostor AO when geometry changed (AO data cache is fresh after cuboid rendering)
    if (this._impostorAODirty && this.impostors.size > 0) {
      this._impostorAODirty = false;
      this.resampleImpostorAO();
    }

    // Determine occlusion using Separating Axis Test
    // Returns: -1 if A is behind B, 1 if B is behind A, 0 if indeterminate
    //
    // Key insight: For A to be definitively "behind" B:
    // - At least one separated axis must indicate A is behind
    // - NO separated axis can indicate B is behind (would be a contradiction)
    // If there's conflicting evidence, use depth-based tiebreaker
    function determineOcclusionSAT(a, b, sinAngle, cosAngle, viewingFromBelow) {
      const EPSILON = 0.0001;

      // Check if cuboids overlap in the X-Z plane (ground plane)
      // This determines whether Y-axis separation is relevant
      const xOverlap = a.minX < b.maxX && b.minX < a.maxX;
      const zOverlap = a.minZ < b.maxZ && b.minZ < a.maxZ;
      const xzPlaneOverlap = xOverlap && zOverlap;

      // Track ordering evidence from each axis
      // -1 = A is behind (further), 1 = B is behind (further), 0 = overlapping/no separation
      let xOrder = 0, zOrder = 0, yOrder = 0;

      // X-axis separation - direction depends on sin(angle)
      // When sin > 0 (Q0, Q1): negative X is "far" (behind)
      // When sin < 0 (Q2, Q3): positive X is "far" (behind)
      if (sinAngle > EPSILON) {
        if (a.maxX <= b.minX) xOrder = -1;      // A has smaller X, A is behind
        else if (b.maxX <= a.minX) xOrder = 1;  // B has smaller X, B is behind
      } else if (sinAngle < -EPSILON) {
        if (a.minX >= b.maxX) xOrder = -1;      // A has larger X, A is behind
        else if (b.minX >= a.maxX) xOrder = 1;  // B has larger X, B is behind
      }

      // Z-axis separation - direction depends on cos(angle)
      // When cos > 0 (Q0, Q3): positive Z (game coords) is "far" (behind)
      // When cos < 0 (Q1, Q2): negative Z (game coords) is "far" (behind)
      if (cosAngle > EPSILON) {
        if (a.minZ >= b.maxZ) zOrder = -1;      // A has larger Z, A is behind
        else if (b.minZ >= a.maxZ) zOrder = 1;  // B has larger Z, B is behind
      } else if (cosAngle < -EPSILON) {
        if (a.maxZ <= b.minZ) zOrder = -1;      // A has smaller Z, A is behind
        else if (b.maxZ <= a.minZ) zOrder = 1;  // B has smaller Z, B is behind
      }

      // Y-axis separation - only relevant if cuboids overlap in XZ plane (potential stacking)
      // Use visual Y bounds when available so sprites with visual extent beyond their
      // sort AABB correctly overlap with nearby cuboids instead of falsely separating
      if (xzPlaneOverlap) {
        const aVisMinY = a.visualMinY !== undefined ? a.visualMinY : a.minY;
        const aVisMaxY = a.visualMaxY !== undefined ? a.visualMaxY : a.maxY;
        const bVisMinY = b.visualMinY !== undefined ? b.visualMinY : b.minY;
        const bVisMaxY = b.visualMaxY !== undefined ? b.visualMaxY : b.maxY;
        if (viewingFromBelow) {
          // Looking UP: higher objects (larger Y) are further away
          if (aVisMinY >= bVisMaxY) yOrder = -1;      // A entirely above B, A is behind
          else if (bVisMinY >= aVisMaxY) yOrder = 1;  // B entirely above A, B is behind
        } else {
          // Looking DOWN: lower objects (smaller Y) are further away
          if (aVisMaxY <= bVisMinY) yOrder = -1;      // A entirely below B, A is behind
          else if (bVisMaxY <= aVisMinY) yOrder = 1;  // B entirely below A, B is behind
        }

        // When cuboids overlap in XZ, Y separation is DOMINANT (vertical stacking)
        if (yOrder !== 0) return yOrder;
      }

      // X-Z SAT verdict for non-XZ-overlapping cuboids, or when Y doesn't separate
      const aIsBehindXZ = (xOrder === -1 ? 1 : 0) + (zOrder === -1 ? 1 : 0);
      const bIsBehindXZ = (xOrder === 1 ? 1 : 0) + (zOrder === 1 ? 1 : 0);

      // Clear XZ verdict: one cuboid is behind on X and/or Z, the other isn't
      if (aIsBehindXZ > 0 && bIsBehindXZ === 0) return -1;
      if (bIsBehindXZ > 0 && aIsBehindXZ === 0) return 1;

      // XZ is conflicting or no separation - use depth tiebreaker
      // Use depth-based tiebreaker: compare "far corners" projected along view direction
      // The far corner is the corner furthest from the camera for each cuboid

      // Determine which corner is "far" based on camera direction
      const aFarX = sinAngle > 0 ? a.minX : a.maxX;
      const aFarZ = cosAngle > 0 ? a.maxZ : a.minZ;
      const aFarY = viewingFromBelow ? a.maxY : a.minY;

      const bFarX = sinAngle > 0 ? b.minX : b.maxX;
      const bFarZ = cosAngle > 0 ? b.maxZ : b.minZ;
      const bFarY = viewingFromBelow ? b.maxY : b.minY;

      // Primary depth from X,Z position (these determine front/back in isometric view)
      const aDepthXZ = -aFarX * sinAngle + aFarZ * cosAngle;
      const bDepthXZ = -bFarX * sinAngle + bFarZ * cosAngle;

      // Y is only a tiebreaker for objects at similar X,Z depth
      // Use a very small factor so height doesn't dominate the calculation
      const Y_TIEBREAKER = 0.001;
      const aDepth = aDepthXZ + aFarY * Y_TIEBREAKER * (viewingFromBelow ? 1 : -1);
      const bDepth = bDepthXZ + bFarY * Y_TIEBREAKER * (viewingFromBelow ? 1 : -1);

      if (aDepth > bDepth + EPSILON) return -1;  // A is further, draw first
      if (bDepth > aDepth + EPSILON) return 1;   // B is further, draw first

      return 0;  // Truly indeterminate (rare - nearly coincident far corners)
    }

  }

  /**
   * Clear all cuboids from the world
   */
  clear() {
    // Clean up audio sources for all video materials before clearing
    for (const cuboid of this.cuboids.values()) {
      this._cleanupCuboidAudioSources(cuboid);
    }
    // Clean up audio sources for group materials (stored on MaterialGroupManager, not on cuboids)
    if (this.materialGroupManager) {
      for (const group of this.materialGroupManager.groups.values()) {
        if (group.material && group.material._cleanupAudioSource) {
          group.material._cleanupAudioSource();
        }
      }
    }
    this.cuboids.clear();
    this.element.innerHTML = '';
    // Clear material groups and mappings
    if (this.materialGroupManager) {
      this.materialGroupManager.groups.clear();
      this.materialGroupManager.faceToGroup.clear();
    }
    // Clear impostors
    for (const impostor of this.impostors.values()) {
      impostor.destroy();
    }
    this.impostors.clear();
    // Clear KD tree fragments and sprite-triggered splits from previous world
    this.kdTree.clear();
    this._spriteTriggeredSplits.clear();
    this.fragmentsNeedClearing = true;
    // Clear AO caches
    AmbientOcclusion.invalidateAll();
    // Clear face occlusion spatial index
    if (FaceOcclusion._spatialIndex) {
      FaceOcclusion._spatialIndex.clear();
    }

    // Notify network sync
    if (this.onMutation) {
      this.onMutation({ op: 'clear', data: {} });
    }
  }

  /**
   * Serialize the world to a JSON-compatible object
   * @param {Object} metadata - Optional metadata (name, author, description)
   * @return {Object} Serializable world data
   */
  toJSON(metadata = {}) {
    const cuboids = [];

    // Serialize all cuboids
    for (const [key, cuboid] of this.cuboids) {
      const cuboidData = {
        x: cuboid.x,
        y: cuboid.y,
        z: cuboid.z,
        width: cuboid.width,
        height: cuboid.height,
        depth: cuboid.depth,
        materials: {}
      };

      // Serialize materials for each face
      const faces = ['top', 'bottom', 'front', 'back', 'left', 'right'];
      faces.forEach(face => {
        if (cuboid.materials[face]) {
          cuboidData.materials[face] = cuboid.materials[face].toJSON();
        }
      });

      cuboids.push(cuboidData);
    }

    // Serialize material groups
    const materialGroups = [];
    if (this.materialGroupManager && this.materialGroupManager.groups) {
      for (const [groupId, group] of this.materialGroupManager.groups) {
        const groupData = {
          id: groupId,
          name: group.name || '',
          // Texture settings
          textureScale: group.textureScale,
          textureScaleLinked: group.textureScaleLinked,
          textureOffset: group.textureOffset,
          textureWidth: group.textureWidth || 256,
          textureHeight: group.textureHeight || 256,
          // Video settings
          videoScale: group.videoScale,
          videoScaleLinked: group.videoScaleLinked,
          videoOffset: group.videoOffset,
          // Gradient settings
          gradientScale: group.gradientScale,
          gradientScaleLinked: group.gradientScaleLinked,
          gradientAngle: group.gradientAngle,
          gradientAxis: group.gradientAxis,
          gradientOffset: group.gradientOffset,
          // Pattern settings
          patternScale: group.patternScale,
          patternScaleLinked: group.patternScaleLinked,
          patternOffset: group.patternOffset,
          patternRotation: group.patternRotation,
          // Color settings
          colorOpacity: group.colorOpacity,
          // Effect settings
          effectScale: group.effectScale,
          effectScaleLinked: group.effectScaleLinked,
          effectOffset: group.effectOffset,
          effectRotation: group.effectRotation,
          // Text settings
          textScale: group.textScale,
          textScaleLinked: group.textScaleLinked,
          textOffset: group.textOffset,
          textAxis: group.textAxis,
          faces: []
        };

        // Serialize the material if present
        if (group.material) {
          groupData.material = group.material.toJSON();
        }

        // Serialize face assignments (group.faces is a Set of serialized strings)
        if (group.faces) {
          for (const serializedFace of group.faces) {
            const separatorIndex = serializedFace.lastIndexOf('|');
            const cuboidKey = serializedFace.substring(0, separatorIndex);
            const face = serializedFace.substring(separatorIndex + 1);
            groupData.faces.push({ cuboidKey, face });
          }
        }

        materialGroups.push(groupData);
      }
    }

    // Serialize NPCs
    const npcs = this.npcManager ? this.npcManager.toJSON() : [];

    // Serialize impostors
    const impostors = [];
    for (const impostor of this.impostors.values()) {
      impostors.push(impostor.toJSON());
    }

    return {
      version: '1.0',
      name: metadata.name || 'Untitled World',
      author: metadata.author || '',
      description: metadata.description || '',
      createdAt: metadata.createdAt || new Date().toISOString(),
      modifiedAt: new Date().toISOString(),
      settings: {
        viewDistance: this.viewDistance,
        chunkSize: this.chunkSize
      },
      cuboids: cuboids,
      materialGroups: materialGroups,
      npcs: npcs,
      impostors: impostors
    };
  }

  /**
   * Load world data from a JSON object
   * @param {Object} data - World data from JSON
   * @param {boolean} clearFirst - Whether to clear existing world first (default: true)
   * @param {Object} options - Loading options
   * @param {boolean} options.batchCuboids - Use addCuboidBatch() for faster loading (skips
   *   per-cuboid onMutation/impostor/navmesh updates — caller must handle). Used by WorldSync.
   */
  fromJSON(data, clearFirst = true, { batchCuboids = false } = {}) {
    if (clearFirst) {
      this.clear();
      // Clear material groups
      if (this.materialGroupManager) {
        this.materialGroupManager.groups.clear();
      }
    }

    // Validate version
    if (!data.version) {
      Debug.warn('World', 'World file missing version, assuming 1.0');
    }

    // Load settings
    if (data.settings) {
      if (data.settings.viewDistance !== undefined) {
        this.viewDistance = data.settings.viewDistance;
      }
      if (data.settings.chunkSize !== undefined) {
        this.chunkSize = data.settings.chunkSize;
      }
    }

    // Load cuboids
    if (data.cuboids && Array.isArray(data.cuboids)) {
      const cuboidDefs = data.cuboids.map(cuboidData => {
        const materials = {};
        if (cuboidData.materials) {
          Object.entries(cuboidData.materials).forEach(([face, materialData]) => {
            materials[face] = Material.fromJSON(materialData);
          });
        }
        return {
          x: cuboidData.x, y: cuboidData.y, z: cuboidData.z,
          width: cuboidData.width || 1, height: cuboidData.height || 1, depth: cuboidData.depth || 1,
          materials
        };
      });

      if (batchCuboids) {
        // Single DOM append, batched AO/occlusion — much faster for network sync.
        // Skips per-cuboid onMutation (suppressed by _isApplyingRemote anyway),
        // validateAllImpostors (loaded separately via _pendingImpostorData),
        // and _notifyGeometryChanged (navmesh rebuilt by main.js after load).
        this.addCuboidBatch(cuboidDefs);
      } else {
        cuboidDefs.forEach(def => {
          this.addCuboid(def.x, def.y, def.z, def.width, def.height, def.depth, def.materials);
        });
      }
    }

    // Load material groups in 3 phases:
    // Phase 1: Create groups, assign faces, pre-populate faceGroups on cuboids
    // Phase 2: Initialize AO + forceRender + update (sets correct face dimensions)
    // Phase 3: Apply group material CSS (triplanar depends on correct face dimensions)
    //
    // Pre-populating faceGroups in Phase 1 ensures that when cuboid.update() runs
    // in Phase 2, Android's refreshMaterials() skips group-managed faces (it checks
    // faceGroups[face] at Cuboid.js:403). Without this, refreshMaterials() would
    // overwrite group faces with per-face materials.

    const mgm = this.materialGroupManager;
    const hasGroups = data.materialGroups && Array.isArray(data.materialGroups) && mgm;

    // Phase 1: Create groups, assign faces, pre-populate faceGroups
    if (hasGroups) {
      mgm.faceToGroup.clear();
      let maxId = 0;

      data.materialGroups.forEach(groupData => {
        const idMatch = groupData.id && groupData.id.match(/^group_(\d+)$/);
        if (idMatch) {
          const numericId = parseInt(idMatch[1], 10);
          if (numericId > maxId) maxId = numericId;
        }

        const group = mgm.createGroup({
          id: groupData.id,
          name: groupData.name || undefined,
          material: groupData.material ? Material.fromJSON(groupData.material) : null,
          textureScale: groupData.textureScale,
          textureScaleLinked: groupData.textureScaleLinked,
          textureOffset: groupData.textureOffset,
          textureWidth: groupData.textureWidth,
          textureHeight: groupData.textureHeight,
          videoScale: groupData.videoScale,
          videoScaleLinked: groupData.videoScaleLinked,
          videoOffset: groupData.videoOffset,
          gradientScale: groupData.gradientScale,
          gradientScaleLinked: groupData.gradientScaleLinked,
          gradientAngle: groupData.gradientAngle,
          gradientAxis: groupData.gradientAxis,
          gradientOffset: groupData.gradientOffset,
          patternScale: groupData.patternScale,
          patternScaleLinked: groupData.patternScaleLinked,
          patternOffset: groupData.patternOffset,
          patternRotation: groupData.patternRotation,
          colorOpacity: groupData.colorOpacity,
          effectScale: groupData.effectScale,
          effectScaleLinked: groupData.effectScaleLinked,
          effectOffset: groupData.effectOffset,
          effectRotation: groupData.effectRotation,
          textScale: groupData.textScale,
          textScaleLinked: groupData.textScaleLinked,
          textOffset: groupData.textOffset,
          textAxis: groupData.textAxis
        });

        if (groupData.faces && Array.isArray(groupData.faces)) {
          mgm.assignFacesToGroup(group.id, groupData.faces);
        }

        // Pre-populate faceGroups on cuboids so refreshMaterials() will skip
        // group-managed faces during the Phase 2 update pass. Normally
        // applyGroupMaterial() sets these (MGM line 814-817), but we defer
        // material CSS application to Phase 3 after face dimensions are set.
        const faces = group.getFaces();
        for (const { cuboidKey, face } of faces) {
          const cuboid = this.cuboids.get(cuboidKey);
          if (cuboid) {
            if (!cuboid.faceGroups) cuboid.faceGroups = {};
            cuboid.faceGroups[face] = group.id;
          }
        }
      });

      if (maxId >= MaterialGroup.nextId) {
        MaterialGroup.nextId = maxId + 1;
      }
    }

    // Phase 2: Initialize AO and run first update pass (sets face dimensions)
    if (AmbientOcclusion.enabled) {
      AmbientOcclusion.initialize(this);
      this._impostorAODirty = true;
    }

    this.cuboids.forEach(cuboid => {
      cuboid.needsUpdate = true;
    });
    this.forceRender();
    this.update();

    // Phase 3: Apply group material CSS now that face dimensions are correct.
    // Triplanar background-position/size depends on face element dimensions set
    // by cuboid.update() in Phase 2. skipWorldUpdate=true since update already ran.
    if (hasGroups && mgm.groups.size > 0) {
      for (const groupId of mgm.groups.keys()) {
        mgm.applyGroupMaterial(groupId, true);
      }
    }

    // Store NPC data for async loading by main.js (after navmesh is built)
    this._pendingNPCData = data.npcs || null;

    // Store impostor data for loading by main.js (after world loaded)
    this._pendingImpostorData = data.impostors || null;

    // Notify listeners that world has been loaded
    if (this.onWorldLoaded) {
      this.onWorldLoaded();
    }

    return {
      name: data.name,
      author: data.author,
      description: data.description,
      createdAt: data.createdAt,
      modifiedAt: data.modifiedAt
    };
  }

  /**
   * Save the world to a downloadable JSON file
   * @param {string} filename - The filename (without extension)
   * @param {Object} metadata - Optional metadata (name, author, description)
   */
  saveToFile(filename = 'world', metadata = {}) {
    const data = this.toJSON(metadata);
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.world.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Load the world from a file (opens file picker)
   * @return {Promise<Object>} Promise resolving to world metadata
   */
  loadFromFile() {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json,.world.json';

      input.onchange = async (event) => {
        const file = event.target.files[0];
        if (!file) {
          reject(new Error('No file selected'));
          return;
        }

        try {
          const text = await file.text();
          const data = JSON.parse(text);
          const metadata = this.fromJSON(data);
          resolve(metadata);
        } catch (error) {
          reject(error);
        }
      };

      input.click();
    });
  }

  /**
   * Load world from a URL
   * @param {string} url - URL to the world JSON file
   * @return {Promise<Object>} Promise resolving to world metadata
   */
  async loadFromURL(url) {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load world: ${response.statusText}`);
    }
    const data = await response.json();
    return this.fromJSON(data);
  }
  
  /**
   * Create a demo world with various cuboids and materials
   */
  createDemoWorld() {
    this.clear();
    
    // Create some example materials
    const glassMaterial = Material.color('rgba(200, 230, 255, 0.5)', 0.5);
    glassMaterial.borderColor = 'rgba(255, 255, 255, 0.3)';
    
    const woodMaterial = Material.texture('textures/wood.jpg');
    const stoneMaterial = Material.texture('textures/stone.jpg');
    const grassMaterial = Material.texture('textures/grass.jpg');
    
    const gradientMaterial = Material.gradient('linear', 
      ['#ff9966', '#ff5e62'], 
      'to bottom');
    
    const stripedMaterial = Material.pattern('stripes', {
      colors: ['#4285f4', '#34a853'],
      direction: '0deg',
      size: '10px'
    });
    
    const gridMaterial = Material.pattern('grid', {
      gridColor: '#ddd',
      backgroundColor: '#fff',
      gridSize: '10px',
      lineWidth: '1px'
    });

    const floorMaterial = Material.texture('textures/floor.jpg', 1.0, true);
    
    // Add a floor with grass texture
    this.addCuboid(-30, -1, -30, 60, 1, 60, {
      top: floorMaterial
    });
    
    // Add some basic cubes with different materials - using standard coordinate system
    this.addCuboid(0, 0, 0, 1, 1, 1, {
      top: gradientMaterial,
      right: stripedMaterial,
      left: gridMaterial
    });
    
    // Glass cube
    this.addCuboid(0, 1, 0, 1, 1, 1, {
      top: glassMaterial,
      right: glassMaterial,
      left: glassMaterial,
      front: glassMaterial,
      back: glassMaterial,
      bottom: glassMaterial
    });
    
    // Wood and stone cubes
    this.addCuboid(0, 2, 0, 1, 1, 1, {
      top: woodMaterial,
      right: stoneMaterial,
      left: stoneMaterial,
      front: woodMaterial,
      back: woodMaterial,
      bottom: stoneMaterial
    });

    this.addCuboid(-2, 3, 0, 3, 1, 8, {
      top: woodMaterial,
      right: stoneMaterial,
      left: stoneMaterial,
      front: woodMaterial,
      back: woodMaterial,
      bottom: stoneMaterial
    });

    // Stone cube culprit 1 /////////////////////
    this.addCuboid(-2, 0, 8, 3, 10, 3, {
      top: stoneMaterial,
      right: stoneMaterial,
      left: stoneMaterial,
      front: stoneMaterial,
      back: stoneMaterial,
      bottom: stoneMaterial
    });

    // Striped cube culprit 2 /////////////////////
    this.addCuboid(4, 0, 2, 2, 5, 18, {
      top: stripedMaterial,
      right: stripedMaterial,
      left: stripedMaterial,
      front: stripedMaterial,
      back: stripedMaterial,
      bottom: stripedMaterial
    });
   
    // Create a video material if supported
    const videoMaterial = Material.video('textures/sample-video.webm', 0.8);
    videoMaterial.borderWidth = 0;
    
    // Add a cube with video on top
    this.addCuboid(1, 0, 0, 3, 5, 2, {
      top: videoMaterial
    });
    
    // Add some cuboids with different dimensions
    //this.addCuboid(4, 0, 2, 2, 5, 7);  // Wide cuboid
    //this.addCuboid(5, 0, 1, 2, 1, 5);  // Deep cuboid
    //this.addCuboid(3, 2, -2, 1, 5, 1); // Tall cuboid
    //this.addCuboid(3, 0, -2, 1, 1, 2); // Deep cuboid
  /*  
    // Add a cube with custom text
    const textMaterial = Material.color('#3498db');
    textMaterial.text = 'Hello World!';
    textMaterial.textColor = '#ffffff';
    textMaterial.textSize = '14px';
    
    this.addCuboid(-3, 0, 0, 2, 1, 2, {
      top: textMaterial
    });

    const floorMaterial = Material.texture('textures/floor.jpg', 1.0, true);
    
    // Add a floor with grass texture
    this.addCuboid(-30, -1, -15, 60, 1, 30, {
      top: floorMaterial
    });
    
    // Add a large cube with different materials on each face
    this.addCuboid(0, 0, 4, 3, 1, 5);
    this.addCuboid(0, 4, -2, 3, 6, 1);
    this.addCuboid(0, 0, -3, 3, 1, 5);

    this.addCuboid(0, 0, 5, 3, 3, 6, {
      top: Material.gradient('radial', ['#fff', '#000'], 'circle'),
      right: Material.pattern('dots', {
        backgroundColor: '#fff',
        dotColor: '#000',
        dotSize: '5px',
        spacing: '15px'
      }),
      front: Material.color('#a74cec'),
      left: videoMaterial
    });
*/
    // EME
    const EMEMaterial = Material.texture('textures/stone.jpg');
    EMEMaterial.borderWidth = 0;
    EMEMaterial.borderColor = '#3498db';
    EMEMaterial.borderStyle = 'solid';

    // 3
    this.addCuboid(-8, 0, -8, 5, 1, 5, {
      top: EMEMaterial,
      front: EMEMaterial,
      right: EMEMaterial,
      back: EMEMaterial,
      left: EMEMaterial,
      bottom: EMEMaterial
    });
    this.addCuboid(-8, 1, -8, 1, 1, 5, {
      top: EMEMaterial,
      front: EMEMaterial,
      right: EMEMaterial,
      back: EMEMaterial,
      left: EMEMaterial,
      bottom: EMEMaterial
    });
    this.addCuboid(-8, 2, -8, 5, 1, 5, {
      top: EMEMaterial,
      front: EMEMaterial,
      right: EMEMaterial,
      back: EMEMaterial,
      left: EMEMaterial,
      bottom: EMEMaterial
    });
    this.addCuboid(-8, 3, -8, 1, 1, 5, {
      top: EMEMaterial,
      front: EMEMaterial,
      right: EMEMaterial,
      back: EMEMaterial,
      left: EMEMaterial,
      bottom: EMEMaterial
    });
    this.addCuboid(-8, 4, -8, 5, 1, 5, {
      top: EMEMaterial,
      front: EMEMaterial,
      right: EMEMaterial,
      back: EMEMaterial,
      left: EMEMaterial,
      bottom: EMEMaterial
    });

    // M
    this.addCuboid(-8, 0, -3, 1, 5, 5, {
      top: EMEMaterial,
      front: EMEMaterial,
      right: EMEMaterial,
      back: EMEMaterial,
      left: EMEMaterial,
      bottom: EMEMaterial
    });
    this.addCuboid(-9, 4, -3, 1, 1, 5, {
     top: EMEMaterial,
     front: EMEMaterial,
     right: EMEMaterial,
     back: EMEMaterial,
     left: EMEMaterial,
     bottom: EMEMaterial
   });
   this.addCuboid(-10, 0, -3, 1, 5, 5, {
     top: EMEMaterial,
     front: EMEMaterial,
     right: EMEMaterial,
     back: EMEMaterial,
     left: EMEMaterial,
     bottom: EMEMaterial
   });
   this.addCuboid(-11, 4, -3, 1, 1, 5, {
     top: EMEMaterial,
     front: EMEMaterial,
     right: EMEMaterial,
     back: EMEMaterial,
     left: EMEMaterial,
     bottom: EMEMaterial
   });
   this.addCuboid(-12, 0, -3, 1, 5, 5, {
     top: EMEMaterial,
     front: EMEMaterial,
     right: EMEMaterial,
     back: EMEMaterial,
     left: EMEMaterial,
     bottom: EMEMaterial
   });

   // E CULPRIT TEST CASE
   this.addCuboid(-18, 0, 2, 5, 1, 5, {//horizontal plane
    top: EMEMaterial,
    front: EMEMaterial,
    right: EMEMaterial,
    back: EMEMaterial,
    left: EMEMaterial,
    bottom: EMEMaterial
  });
  this.addCuboid(-14, 1, 2, 1, 1, 5, {//slim bar
   top: EMEMaterial,
   front: EMEMaterial,
   right: EMEMaterial,
   back: EMEMaterial,
   left: EMEMaterial,
   bottom: EMEMaterial
 });
 /*this.addCuboid(-18, 2, 2, 5, 1, 5, {//horizontal plane
   top: EMEMaterial,
   front: EMEMaterial,
   right: EMEMaterial,
   back: EMEMaterial,
   left: EMEMaterial,
   bottom: EMEMaterial
 });
 this.addCuboid(-14, 3, 2, 1, 1, 5, {//slim bar
   top: EMEMaterial,
   front: EMEMaterial,
   right: EMEMaterial,
   back: EMEMaterial,
   left: EMEMaterial,
   bottom: EMEMaterial
 });
 this.addCuboid(-18, 4, 2, 5, 1, 5, {//horizontal plane
   top: EMEMaterial,
   front: EMEMaterial,
   right: EMEMaterial,
   back: EMEMaterial,
   left: EMEMaterial,
   bottom: EMEMaterial
 });*/

    // Apply multi-face material groups to the letters
    this.applyMultiFaceMaterialsToLetters();

    // Notify listeners that world has been loaded
    if (this.onWorldLoaded) {
      this.onWorldLoaded();
    }
  }

  /**
   * Apply multi-face material groups to the M and E letter cuboids
   * This creates seamless textures across all letter faces
   */
  applyMultiFaceMaterialsToLetters() {
    const groupManager = this.materialGroupManager;

    // Create stone material with no border for seamless transitions
    const stoneMaterial = Material.texture('textures/stone.jpg');
    stoneMaterial.borderWidth = 0;

    // Create separate groups for each letter
    const eGroup = groupManager.createGroup({
      name: 'E Letter (First)',
      material: stoneMaterial,
      textureScale: 1.0,
      textureWidth: 256,
      textureHeight: 256
    });

    const mGroup = groupManager.createGroup({
      name: 'M Letter',
      material: stoneMaterial,
      textureScale: 1.0,
      textureWidth: 256,
      textureHeight: 256
    });

    const partialEGroup = groupManager.createGroup({
      name: 'E Letter (Partial)',
      material: stoneMaterial,
      textureScale: 1.0,
      textureWidth: 256,
      textureHeight: 256
    });

    const faces = ['top', 'front', 'back', 'left', 'right', 'bottom'];

    // Find all letter cuboids and add their visible faces to appropriate groups
    for (const [key, cuboid] of this.cuboids) {
      // First E letter (the "3" shape): x=-8, z=-8
      const isFirstE = (cuboid.x === -8 && cuboid.z === -8);

      // M letter: x from -8 to -12, z=-3
      const isMLetter = (cuboid.x >= -12 && cuboid.x <= -8 && cuboid.z === -3);

      // Partial E letter: x from -18 to -14, z=2
      const isPartialE = (cuboid.x >= -18 && cuboid.x <= -14 && cuboid.z === 2);

      if (isFirstE) {
        faces.forEach(face => {
          groupManager.assignFacesToGroup(eGroup.id, [{ cuboidKey: key, face }]);
        });
      } else if (isMLetter) {
        faces.forEach(face => {
          groupManager.assignFacesToGroup(mGroup.id, [{ cuboidKey: key, face }]);
        });
      } else if (isPartialE) {
        faces.forEach(face => {
          groupManager.assignFacesToGroup(partialEGroup.id, [{ cuboidKey: key, face }]);
        });
      }
    }

    // Apply the group materials with triplanar projection
    groupManager.applyGroupMaterial(eGroup.id);
    groupManager.applyGroupMaterial(mGroup.id);
    groupManager.applyGroupMaterial(partialEGroup.id);

    // Force all cuboids to update to ensure transforms are properly applied
    // This is necessary because applyGroupMaterial clears face styles
    this.cuboids.forEach(cuboid => {
      cuboid.needsUpdate = true;
    });
    this.forceRender();
    this.update();
  }
}